import type { MenuProps } from "antd";
import {
  AccountBookOutlined,
  ApartmentOutlined,
  AppstoreOutlined,
  AuditOutlined,
  BankOutlined,
  BarcodeOutlined,
  BgColorsOutlined,
  CalendarOutlined,
  CarOutlined,
  CloudServerOutlined,
  ContactsOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DollarOutlined,
  FileTextOutlined,
  GlobalOutlined,
  HistoryOutlined,
  IdcardOutlined,
  InboxOutlined,
  KeyOutlined,
  LineChartOutlined,
  LockOutlined,
  MessageOutlined,
  MoneyCollectOutlined,
  NotificationOutlined,
  PartitionOutlined,
  PieChartOutlined,
  PrinterOutlined,
  ProjectOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  TagsOutlined,
  TeamOutlined,
  TrophyOutlined,
  UserOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { MenuLabel } from "./MenuLabel";
import { ROUTES } from "@/constants/routes";
import {
  ATTRIBUTE_GATE,
  BRAND_GATE,
  CATEGORY_GATE,
  UNIT_GATE,
} from "@/constants/productCatalogueGates";
import type { StaffRole } from "@/redux/slices/authSlice";
import type { PlanTier } from "@/redux/slices/planSlice";
import { PLAN_RANK } from "@/redux/slices/planSlice";
// TCR-2542 — gate nhóm menu "Trung tâm báo cáo" theo hợp OR key của report registry.
import { REPORT_CENTER_ANY_PERMISSIONS } from "@/pages/ReportCenter/reportRegistry";

function labelWithTier(label: string, _tier?: "L" | "S" | "P" | "E") {
  return label;
}

const ACCOUNTING_SECTION_ACCENTS: Record<string, string> = {
  "accounting-section-overview": "#60a5fa",
  "accounting-section-sales": "#22c55e",
  "accounting-section-purchase": "#f59e0b",
  "accounting-section-invoices": "#a78bfa",
  "accounting-section-cash-bank": "#06b6d4",
  "accounting-section-inventory": "#10b981",
  "accounting-section-assets": "#f97316",
  "accounting-section-debt": "#f43f5e",
  "accounting-section-reports": "#38bdf8",
  "accounting-section-amis": "#ec4899",
  "accounting-section-settings": "#94a3b8",
};

function accountingSectionLabel(key: string, label: string) {
  return (
    <span className="erp-accounting-section-label">
      <span
        className="erp-accounting-section-dot"
        style={{
          backgroundColor: ACCOUNTING_SECTION_ACCENTS[key] || "#94a3b8",
        }}
      />
      {/*
              Nhãn nhóm cũng bị cắt cụt (TCR-1215): trên bản chụp của ticket
              "TIỀN MẶT, NGÂN HÀNG" hiện thành "TIỀN MẶT, NGÂN HÀN(". Bọc riêng ở
              đây vì nhãn này là JSX tự dựng, không đi qua chỗ bọc chung ở Sidebar.
            */}
      <MenuLabel text={label} />
    </span>
  );
}

export type SidebarMenuItems = NonNullable<MenuProps["items"]>;
export type SidebarMenuItem = SidebarMenuItems[number] & {
  drilldown?: boolean;
};

interface BuildMenuOpts {
  // Nhận cả mảng (OR) — khớp `useRbac().hasPermission` sau PR3, phục vụ giai đoạn chuyển
  // tiếp `staff.*` -> `hrm.*` (call site truyền mảng gồm key hrm mới + key staff cũ).
  hasPermission: (key: string | readonly string[]) => boolean;
  onNavigate: (path: string) => void;
  /**
   * Trỏ thẳng vào `StaffRole` thay vì chép lại danh sách vai trò.
   *
   * Bản chép tay ở đây là bản THỨ TƯ của cùng một union (authSlice, useAuth,
   * mocks/staffs và chỗ này), và nó lệch nhau: thêm `shipper` cho TCR-1521 làm
   * vỡ đúng hai chỗ dùng file này. Trỏ vào một nguồn thì lần sau thêm vai trò
   * không phải đi sửa vòng quanh.
   */
  userRole?: StaffRole;
  /** TCR-2075 — mọi vai trò của tài khoản (chính + bổ sung); ưu tiên hơn `userRole` khi có. */
  userRoles?: readonly string[];
  planTier?: PlanTier;
  bulkPackingEnabled?: boolean;
}

function meetsTierRequirement(
  currentTier: PlanTier,
  requiredTier: PlanTier,
): boolean {
  return PLAN_RANK[currentTier] >= PLAN_RANK[requiredTier];
}

/** Threshold số children → popup multi-column (2 cột) trên hover desktop. */
const MULTICOL_THRESHOLD = 10;

/**
 * TCR-1534 — tạm ẩn group "Lương" khỏi sidebar. Route `/payroll/*` vẫn giữ,
 * gõ URL trực tiếp vẫn vào được. Đổi `true` để hiện lại menu.
 */
export const PAYROLL_MENU_ENABLED = false;

function countMenuEntries(items: SidebarMenuItems): number {
  return items.filter(
    (item) => item && (item as { type?: string }).type !== "divider",
  ).length;
}

/**
 * Sidebar menu structure theo SITEMAP v3.0 (15 groups). Gom gọn từ ~25 nhóm
 * legacy → 12-15 nhóm chính. Group có nhiều children → popup multi-column
 * (CSS class `erp-menu-popup-wide` ở Sidebar/index.tsx) để giảm chiều dài.
 *
 * Lookup SITEMAP: `ERP-docs/tenant-app/SITEMAP.md` §1-15.
 */
export function buildSidebarMenuItems({
  hasPermission,
  onNavigate,
  userRole,
  userRoles,
  planTier = "lite",
  bulkPackingEnabled = false,
}: BuildMenuOpts): SidebarMenuItems {
  const items: SidebarMenuItems = [];
  const isOwner = userRole === "owner";
  const isManagerPlus =
    isOwner || (userRoles ?? (userRole ? [userRole] : [])).includes("manager");
  const hasStarter = meetsTierRequirement(planTier, "starter");
  const hasProfessional = meetsTierRequirement(planTier, "professional");

  // Nhãn group có nhiều children → enable popup multi-column.
  const maybeWidePopup = (
    label: string,
    children: SidebarMenuItems,
    tier?: "L" | "S" | "P" | "E",
  ) => ({
    label: labelWithTier(label, tier),
    popupClassName:
      countMenuEntries(children) > MULTICOL_THRESHOLD
        ? "erp-menu-popup-wide"
        : undefined,
  });

  // ─── §1 Dashboard ─────────────────────────────────────────────────────────
  items.push({
    key: ROUTES.dashboard,
    icon: <DashboardOutlined />,
    label: "Tổng quan",
    onClick: () => onNavigate(ROUTES.dashboard),
  });

  // ─── TCR-2542 — Trung tâm báo cáo ─────────────────────────────────────────
  // Gate: hợp OR mọi permission key trong report registry (REPORT_CENTER_ANY_PERMISSIONS,
  // hiện là 'reports.*' của catalogue) — ai thấy được ÍT NHẤT 1 báo cáo thì thấy mục
  // này. Thêm báo cáo mới vào registry là mục tự đúng quyền, KHÔNG sửa lại gate ở đây.
  // Đặt ngay dưới Tổng quan: báo cáo là đích đến hàng tuần của chủ shop, không chôn
  // dưới nhóm khác.
  if (hasPermission(REPORT_CENTER_ANY_PERMISSIONS)) {
    items.push({
      key: ROUTES.reportCenter.index,
      icon: <PieChartOutlined />,
      label: "Trung tâm báo cáo",
      onClick: () => onNavigate(ROUTES.reportCenter.index),
    });
  }

  // ─── §2 Bán hàng ──────────────────────────────────────────────────────────────────
  // Chỉ hiển thị các luồng bán hàng phù hợp phạm vi TCR-ERP.
  const salesChildren: SidebarMenuItems = [];
  if (hasPermission("orders.view")) {
    salesChildren.push({
      key: ROUTES.orders.list,
      label: "Đơn hàng",
      onClick: () => onNavigate(ROUTES.orders.list),
    });
  }
  if (hasPermission("pos.return_requests.view")) {
    salesChildren.push({
      key: ROUTES.posReturns,
      label: "Đơn yêu cầu hoàn",
      onClick: () => onNavigate(ROUTES.posReturns),
    });
  }
  // TẠM TẮT (TCR-593): TCR chưa dùng ca làm việc — ẩn khỏi menu, route /pos/shifts vẫn giữ.
  // if (hasPermission('pos.shifts_manage')) {
  //     salesChildren.push({
  //         key: ROUTES.posShifts.list,
  //         label: 'Ca làm việc',
  //         onClick: () => onNavigate(ROUTES.posShifts.list),
  //     });
  // }

  if (salesChildren.length > 0) {
    items.push({
      key: "sales-group",
      icon: <ShoppingCartOutlined />,
      ...maybeWidePopup("Bán hàng", salesChildren),
      drilldown: true,
      children: salesChildren,
    } as SidebarMenuItem);
  }

  // Mở rộng gate khối menu để các quyền ECom chuyên biệt và quyền đóng hàng vẫn thấy đúng
  // nhóm con dù không có 'orders.view'. Mỗi nhóm con tiếp tục gate theo permission riêng.
  if (
    hasPermission("orders.view") ||
    hasPermission("pos.orders.view") ||
    hasPermission("settings.manage") ||
    hasPermission("ecom.orders.view") ||
    hasPermission("ecom.reconciliation.returned_orders.view") ||
    hasPermission("ecom.reconciliation.payouts.view") ||
    hasPermission("ecom.reports.view") ||
    hasPermission("ecom.return_cancel_tracking.view") ||
    hasPermission("ecom.return_cancel_tracking.warehouse.manage") ||
    hasPermission("ecom.return_cancel_tracking.accounting.manage") ||
    hasPermission("fulfillment.packing.view")
  ) {
    const ecomChildren: SidebarMenuItems = [];
    const fulfillmentChildren: SidebarMenuItems = [];

    // RBAC fix (2026-09-01) — trang "Chuẩn bị hàng" gộp 2 khu vực quyền khác nhau: panel "Đi
    // đơn" (tạo wave, role owner/manager/inventory) và panel "Xuất bán hàng"/"In phiếu" (gated
    // BE bằng fulfillment.init_logistic.{view,manage}, dành cho Kế toán Kho). Trước fix này menu
    // chỉ check 'orders.view' — nv_kho (có orders.view nhưng KHÔNG còn init_logistic sau khi
    // narrow) vẫn thấy menu + nút "Đi đơn"/"Xuất bán hàng" dù bấm vào sẽ 403 (đúng là ảnh chụp
    // màn hình user gửi cho thấy). Ẩn hẳn menu item này khi thiếu CẢ 2 quyền liên quan — user có
    // fulfillment.init_logistic.view (owner/manager/inventory/accountant/ke_toan_truong) vẫn thấy
    // để dùng ít nhất panel "Xuất bán hàng"; nv_kho không còn thấy nữa.
    // TCR-1590 — route BE (init-logistic/orders) nay OR-gate '.view'/'.manage' (xem
    // oc-fulfillment.controller.ts), gate menu theo đúng cặp key này để role chỉ có '.manage'
    // (vd tích riêng "Quản lý chuẩn bị hàng" qua Cài đặt > Vai trò/quyền) cũng thấy menu.
    if (
      hasPermission("fulfillment.init_logistic.view") ||
      hasPermission("fulfillment.init_logistic.manage")
    ) {
      fulfillmentChildren.push({
        key: ROUTES.ecom.fulfillment.initLogistic,
        label: "Chuẩn bị hàng",
        onClick: () => onNavigate(ROUTES.ecom.fulfillment.initLogistic),
      });
    }
    // RBAC fix (2026-09-02, Task F3) — "Đóng hàng" trước đây ăn theo gate ngoài 'orders.view'/
    // 'settings.manage' của cả khối (không có gate riêng), trong khi route/API thật của trang
    // này (fulfillment/order-packing) đòi đúng 'fulfillment.packing.view' (xem App.tsx cùng
    // batch) — 5 role có 'orders.view' nhưng KHÔNG có 'fulfillment.packing.view'
    // (b2b_sales_manager/accountant/truong_nhom_kd/gd_kd/ke_toan_truong, xem
    // role-permissions.service.ts) trước đây thấy mục này nhưng bấm vào sẽ 403 (API thật đòi key
    // packing, không phải orders.view) — nay route guard đã đổi sang đúng key nên gate riêng
    // menu theo đúng key đó để không dẫn user vào màn chắc chắn lỗi.
    if (hasPermission("fulfillment.packing.view")) {
      fulfillmentChildren.push({
        key: ROUTES.ecom.fulfillment.orderPacking,
        label: "Đóng hàng",
        onClick: () => onNavigate(ROUTES.ecom.fulfillment.orderPacking),
      });
    }
    // "Đóng gói hàng loạt" (wave-packing) được điều khiển bởi setting chung fulfillment; khi
    // bật mới đưa mục vào menu. TCR-1589 (2026-09-05) — API wave (EcomFulfillmentWaveController)
    // đã migrate từ RolesGuard sang PermissionsGuard ('fulfillment.wave.view'/'.operate'/
    // '.manage'), nay gate thêm theo đúng 3 key đó (trước đây chỉ theo feature-flag, khiến
    // role có flag bật nhưng thiếu mọi quyền wave vẫn thấy menu rồi 403 khi bấm vào).
    if (
      bulkPackingEnabled &&
      (hasPermission("fulfillment.wave.view") ||
        hasPermission("fulfillment.wave.operate") ||
        hasPermission("fulfillment.wave.manage"))
    ) {
      fulfillmentChildren.push({
        key: ROUTES.ecom.fulfillment.wavePacking,
        label: "Đóng gói hàng loạt",
        onClick: () => onNavigate(ROUTES.ecom.fulfillment.wavePacking),
      });
    }
    // RBAC fix (2026-09-02, Task F2.1) — "Gửi hàng" trước đây ăn theo gate ngoài
    // 'orders.view'/'settings.manage' của cả khối (không có gate riêng), trong khi route/API
    // thật của trang này (fulfillment/order-sending) đòi đúng 'fulfillment.sending.view' — 2
    // role có orders.view nhưng KHÔNG có fulfillment.sending.view (truong_nhom_kd/gd_kd/
    // ke_toan_truong/b2b_sales_manager, xem role-permissions.service.ts) trước đây thấy mục
    // này nhưng bấm vào sẽ 403 sau khi App.tsx route guard cũng đổi sang key này (xem commit
    // cùng batch). Gate riêng theo đúng permission route thật yêu cầu.
    if (hasPermission("fulfillment.sending.view")) {
      fulfillmentChildren.push({
        key: ROUTES.ecom.fulfillment.orderSending,
        label: "Gửi hàng",
        onClick: () => onNavigate(ROUTES.ecom.fulfillment.orderSending),
      });
    }

    // TCR-1593 — key mới 'ecom.settings.manage' cấp riêng cho nv_sale_ecom/ecom_sales_manager,
    // OR-gate với 'settings.manage' gốc (Owner/Manager), khớp đúng route guard App.tsx.
    if (
      hasPermission("settings.manage") ||
      hasPermission("ecom.settings.manage")
    ) {
      fulfillmentChildren.push({
        key: ROUTES.ecom.fulfillment.settings,
        label: "Cài đặt",
        onClick: () => onNavigate(ROUTES.ecom.fulfillment.settings),
      });
    }

    // TCR-1601 (2026-09-05) — 3 nhóm dưới đây trước đây gate CHUNG bằng 'orders.view' (dot
    // key POS orders) nên bất kỳ role nào có key đó (accountant, inventory, nv_kho...) đều
    // thấy được dù không liên quan ECom. Tách gate theo đúng permission BE thật đang yêu cầu:
    // "Đơn ECom"/"Đơn huỷ" → 'ecom.orders.view' (đã có từ trước); "Đơn hoàn"/"Đối soát sàn" →
    // 'ecom.reconciliation.*' (key MỚI, tách đôi ở TCR-1723 — xem dưới); "Báo cáo" (6 trang)
    // → 'ecom.reports.view' (key MỚI).
    if (hasPermission("ecom.orders.view")) {
      ecomChildren.push({
        key: ROUTES.ecom.orders,
        label: "Đơn ECom",
        onClick: () => onNavigate(ROUTES.ecom.orders),
      });
    }

    // TCR-658: gom toàn bộ báo cáo Ecom vào 1 submenu "Báo cáo", đặt sau
    // "Kiểm tra, đối soát" thay vì đổ phẳng ra menu ECom.
    const reconciliationChildren: SidebarMenuItems = [];
    if (hasPermission("ecom.orders.view")) {
      reconciliationChildren.push({
        key: ROUTES.ecom.cancelOrders,
        label: "Đơn huỷ",
        onClick: () => onNavigate(ROUTES.ecom.cancelOrders),
      });
    }
    // TCR-1723 (2026-09-10) — hai mục dưới đây trước gate CHUNG bằng
    // 'ecom.reconciliation.view', nên không có cách nào cho thủ kho xem đơn hoàn (để nhận
    // hàng về nhập kho) mà không lộ luôn đối soát thanh toán — dữ liệu dòng tiền. Nay mỗi
    // mục gate bằng nhánh key riêng của chính nó.
    if (hasPermission("ecom.reconciliation.returned_orders.view")) {
      reconciliationChildren.push({
        key: ROUTES.ecom.returns,
        label: "Đơn hoàn",
        onClick: () => onNavigate(ROUTES.ecom.returns),
      });
    }
    if (hasPermission("ecom.reconciliation.payouts.view")) {
      reconciliationChildren.push({
        key: ROUTES.ecom.reconciliation,
        label: "Đối soát sàn",
        onClick: () => onNavigate(ROUTES.ecom.reconciliation),
      });
    }
    if (reconciliationChildren.length > 0) {
      ecomChildren.push({
        key: "ecom-reconciliation-group",
        label: "Kiểm tra, đối soát",
        children: reconciliationChildren,
      });
    }

    if (hasPermission("ecom.reports.view")) {
      ecomChildren.push({
        key: "ecom-reports-group",
        label: "Báo cáo",
        children: [
          {
            key: ROUTES.ecom.reports,
            label: "Báo cáo chung",
            onClick: () => onNavigate(ROUTES.ecom.reports),
          },
          {
            key: ROUTES.ecom.performance,
            label: "Báo cáo hiệu suất",
            onClick: () => onNavigate(ROUTES.ecom.performance),
          },
          {
            key: ROUTES.ecom.productsReport,
            label: "Báo cáo sản phẩm",
            onClick: () => onNavigate(ROUTES.ecom.productsReport),
          },
          {
            key: ROUTES.ecom.salesStats,
            label: "Thống kê doanh thu",
            onClick: () => onNavigate(ROUTES.ecom.salesStats),
          },
          {
            key: ROUTES.ecom.revenueReport,
            label: "Báo cáo doanh thu",
            onClick: () => onNavigate(ROUTES.ecom.revenueReport),
          },
          {
            key: ROUTES.ecom.expenseReport,
            label: "Báo cáo chi phí",
            onClick: () => onNavigate(ROUTES.ecom.expenseReport),
          },
          {
            key: ROUTES.ecom.returnCancelTracking,
            label: "Theo dõi Đơn hàng Ecom Hoàn, Huỷ",
            onClick: () => onNavigate(ROUTES.ecom.returnCancelTracking),
          },
          // TCR-2481 — "Nhân viên": thống kê đi đơn/đóng gói/bàn giao/nhận hoàn + tạo đơn theo NV.
          {
            key: ROUTES.ecom.staffStats,
            label: "Nhân viên",
            onClick: () => onNavigate(ROUTES.ecom.staffStats),
          },
          // TCR-2094, Task 7 — mục mới "Báo cáo TikTok Shop theo ngày", đặt cuối nhóm Báo cáo,
          // cùng gate 'ecom.reports.view' như 6 mục báo cáo khác ở trên (không gate riêng).
          {
            key: ROUTES.ecom.tiktokDailyReport,
            label: "Báo cáo TikTok Shop theo ngày",
            onClick: () => onNavigate(ROUTES.ecom.tiktokDailyReport),
          },
          // TCR-2095, Task 8 — mục mới "Báo cáo Shopee theo ngày", cùng gate 'ecom.reports.view'
          // như mục TikTok Shop ở trên (không gate riêng).
          {
            key: ROUTES.ecom.shopeeDailyReport,
            label: "Báo cáo Shopee theo ngày",
            onClick: () => onNavigate(ROUTES.ecom.shopeeDailyReport),
          },
        ],
      });
    }
    if (
      !hasPermission("ecom.reports.view") &&
      (hasPermission("ecom.return_cancel_tracking.view") ||
        hasPermission("ecom.return_cancel_tracking.warehouse.manage") ||
        hasPermission("ecom.return_cancel_tracking.accounting.manage"))
    ) {
      ecomChildren.push({
        key: "ecom-reports-group",
        label: "Báo cáo",
        children: [
          {
            key: ROUTES.ecom.returnCancelTracking,
            label: "Theo dõi Đơn hàng Ecom Hoàn, Huỷ",
            onClick: () => onNavigate(ROUTES.ecom.returnCancelTracking),
          },
        ],
      });
    }
    // TCR-1593 — cùng lý do đã ghi ở mục "Cài đặt" trong nhóm Chuẩn bị hàng phía trên.
    if (
      hasPermission("settings.manage") ||
      hasPermission("ecom.settings.manage")
    ) {
      ecomChildren.push({
        key: ROUTES.ecom.settings,
        label: "Cài đặt",
        onClick: () => onNavigate(ROUTES.ecom.settings),
      });
    }
    // TCR-2094/2095 — gate riêng bằng đúng permission key BE thật yêu cầu
    // ('ecom.business_brands.manage'), không dùng chung gate 'settings.manage' ở trên.
    if (hasPermission("ecom.business_brands.manage")) {
      ecomChildren.push({
        key: ROUTES.ecom.businessBrands,
        label: "Nhãn hàng kinh doanh",
        onClick: () => onNavigate(ROUTES.ecom.businessBrands),
      });
    }
    if (ecomChildren.length > 0) {
      items.push({
        key: "ecom-group",
        icon: <GlobalOutlined />,
        ...maybeWidePopup("ECom", ecomChildren, "S"),
        drilldown: true,
        children: ecomChildren,
      } as SidebarMenuItem);
    }

    if (fulfillmentChildren.length > 0) {
      items.push({
        key: "fulfillment-group",
        icon: <InboxOutlined />,
        ...maybeWidePopup("Xử lý đơn hàng", fulfillmentChildren),
        drilldown: true,
        children: fulfillmentChildren,
      } as SidebarMenuItem);
    }
  }

  // RBAC fix (2026-09-02, Task F2.1) — shipper có 'fulfillment.sending.view'/'.manage' nhưng
  // KHÔNG có 'orders.view'/'settings.manage' nên rơi ra NGOÀI khối "Xử lý đơn hàng" phía trên
  // (gate ngoài của cả khối) — trước fix này shipper chưa từng thấy "Gửi hàng" dù BE đã cấp đủ
  // quyền (audit gốc). Nhánh riêng biệt này CHỈ thêm đúng 1 mục "Gửi hàng" — cố ý KHÔNG kéo theo
  // "Đóng hàng"/"Đóng gói hàng loạt" (2 mục đó thuộc phạm vi "Chuẩn bị hàng"/"Đóng hàng" mà Excel
  // + comment khối `shipper` trong role-permissions.service.ts (BE) nói rõ shipper KHÔNG được
  // đụng tới). Điều kiện loại trừ `!orders.view && !settings.manage` đảm bảo nhánh này KHÔNG bao
  // giờ chạy song song với khối phía trên (tránh trùng key 'fulfillment-group').
  if (
    !hasPermission("orders.view") &&
    !hasPermission("settings.manage") &&
    hasPermission("fulfillment.sending.view")
  ) {
    items.push({
      key: "fulfillment-group",
      icon: <InboxOutlined />,
      label: "Xử lý đơn hàng",
      drilldown: true,
      children: [
        {
          key: ROUTES.ecom.fulfillment.orderSending,
          label: "Gửi hàng",
          onClick: () => onNavigate(ROUTES.ecom.fulfillment.orderSending),
        },
      ],
    } as SidebarMenuItem);
  }

  // Omnichat — inbox, worklist chăm sóc và cấu hình rule dùng chung một nhóm.
  if (
    hasPermission("omnichat.conversations.view") ||
    hasPermission("omnichat.conversations.view_all") ||
    hasPermission("omnichat.conversations.manage") ||
    hasPermission("omnichat.channels.manage") ||
    hasPermission("omnichat.campaigns.manage") ||
    hasPermission("omnichat.ticket_categories.manage") ||
    hasPermission("omnichat.canned_responses.manage") ||
    hasPermission("omnichat.care_rules.manage") ||
    hasPermission("omnichat.sla.view") ||
    hasPermission("omnichat.sla.manage")
  ) {
    const omnichatChildren: SidebarMenuItems = [];
    if (
      hasPermission("omnichat.conversations.view") ||
      hasPermission("omnichat.conversations.view_all") ||
      hasPermission("omnichat.conversations.manage")
    ) {
      omnichatChildren.push({
        key: ROUTES.omnichat.inbox,
        label: "Tin nhắn",
        onClick: () => onNavigate(ROUTES.omnichat.inbox),
      });
    }
    if (
      hasPermission("omnichat.conversations.view") ||
      hasPermission("omnichat.conversations.manage") ||
      hasPermission("omnichat.care_rules.manage")
    ) {
      omnichatChildren.push({
        key: ROUTES.omnichat.care,
        label: "Việc cần chăm sóc",
        onClick: () => onNavigate(ROUTES.omnichat.care),
      });
    }
    if (hasPermission("omnichat.care_rules.manage")) {
      omnichatChildren.push({
        key: ROUTES.omnichat.careRules,
        label: "Quy tắc chăm sóc",
        onClick: () => onNavigate(ROUTES.omnichat.careRules),
      });
    }
    if (hasPermission("omnichat.campaigns.manage")) {
      omnichatChildren.push({
        key: ROUTES.omnichat.campaigns,
        label: "Campaign",
        onClick: () => onNavigate(ROUTES.omnichat.campaigns),
      });
    }
    if (hasPermission("omnichat.sla.view") || hasPermission("omnichat.sla.manage")) {
      omnichatChildren.push({
        key: ROUTES.omnichat.slaReport,
        label: "Báo cáo SLA",
        onClick: () => onNavigate(ROUTES.omnichat.slaReport),
      });
    }
    if (
      hasPermission("omnichat.ticket_categories.manage") ||
      hasPermission("omnichat.canned_responses.manage") ||
      hasPermission("omnichat.care_rules.manage") ||
      hasPermission("omnichat.channels.manage") ||
      hasPermission("omnichat.sla.view") ||
      hasPermission("omnichat.sla.manage")
    ) {
      omnichatChildren.push({
        key: ROUTES.omnichat.settings,
        label: "Cài đặt Omnichat",
        onClick: () => onNavigate(ROUTES.omnichat.settings),
      });
    }
    items.push({
      key: "omnichat-group",
      icon: <MessageOutlined />,
      ...maybeWidePopup("Omni Chat", omnichatChildren),
      drilldown: true,
      children: omnichatChildren,
    } as SidebarMenuItem);
  }

  // TCR-1766 — 'b2b.access' là CỔNG VÀO phân hệ B2B (catalogue: "Quyền cổng vào bắt buộc…",
  // key chỉ FE enforce, không controller BE nào đọc — cùng kiểu 'pos.use'). Gate ngoài phải là
  // ĐÚNG MỘT điều kiện này, nếu không bỏ tick nó chẳng ẩn được gì:
  //   - nhánh OR 'b2b.staff_inventory.view' (thêm ở TCR-1587 cho thủ kho) là đúng lỗi QA báo:
  //     role "NVBH B2B" bỏ tick 'b2b.access' nhưng còn tick key này vẫn thấy nguyên nhóm. 4 role
  //     hệ thống từng dựa vào nhánh đó (truong_nhom_kd/gd_kd/ecom_sales_manager/nv_sale_ecom)
  //     nay được cấp thẳng 'b2b.access' ở BE (role-permissions.service.ts + migration
  //     backfill-tcr-1766-b2b-access-permission.ts) nên giữ nguyên mục "Tồn kho nhân viên".
  //     Thủ kho (`inventory`) có 'b2b.access' trong code từ lâu nhưng doc role trong Mongo của
  //     tenant cũ thì KHÔNG (seed `$setOnInsert` không bao giờ cập nhật doc đã có) — migration
  //     nói trên vá luôn cả trường hợp drift này, không chỉ 4 role trên.
  //   - `isManagerPlus` cũng bỏ: owner có '*', manager có 'b2b.*' nên đã pass qua
  //     hasRuntimePermission; giữ lại thì chủ shop gỡ tick 'b2b.access' của role manager vẫn
  //     không ẩn được menu — đúng thứ ticket yêu cầu phải ẩn.
  // Group rỗng (có 'b2b.access' nhưng không key con nào) đã được dropEmptyGroups() lọc.
  if (hasPermission("b2b.access")) {
    const b2bChildren: SidebarMenuItems = [];
    // 1. Khách hàng + Quan hệ KH (luồng: KH trước → bán sau)
    // BE gate GET /v1/b2b/customers... bằng 'b2b.customers.view' — key duy nhất, đủ cho mọi
    // role. TCR-1770 — bỏ OR-alternative 'b2b.crm.view' cũ (quyền legacy đã xoá khỏi hệ thống).
    if (hasPermission("b2b.customers.view")) {
      b2bChildren.push(
        {
          key: ROUTES.b2b.customers,
          label: "Khách hàng",
          onClick: () => onNavigate(ROUTES.b2b.customers),
        },
        {
          key: ROUTES.b2b.crm,
          label: "Quan hệ khách hàng",
          onClick: () => onNavigate(ROUTES.b2b.crm),
        },
      );
    }
    // 2. Bảng giá + Chiết khấu (chính sách giá)
    if (hasPermission("b2b.pricing.view"))
      b2bChildren.push({
        key: ROUTES.b2b.pricing.list,
        label: "Bảng giá",
        onClick: () => onNavigate(ROUTES.b2b.pricing.list),
      });
    if (hasPermission("b2b.rebates.view"))
      b2bChildren.push({
        key: ROUTES.b2b.rebates.list,
        label: "Chiết khấu tích lũy",
        onClick: () => onNavigate(ROUTES.b2b.rebates.list),
      });
    // 3. Tồn kho nhân viên (vận hành) — TCR-1587: trước đây push không điều kiện, hiện với
    // MỌI role vào được nhóm B2B kể cả role không có quyền xem mục này.
    if (hasPermission("b2b.staff_inventory.view"))
      b2bChildren.push({
        key: ROUTES.b2b.staffInventory.list,
        label: "Tồn kho nhân viên",
        onClick: () => onNavigate(ROUTES.b2b.staffInventory.list),
      });
    // 4. Công nợ (theo dõi tài chính)
    if (hasPermission("b2b.receivables.view"))
      b2bChildren.push({
        key: ROUTES.b2b.receivables,
        label: "Công nợ",
        onClick: () => onNavigate(ROUTES.b2b.receivables),
      });
    if (hasPermission("b2b.gift_debts.view"))
      b2bChildren.push({
        key: ROUTES.b2b.giftDebts,
        label: "Nợ quà tặng",
        onClick: () => onNavigate(ROUTES.b2b.giftDebts),
      });
    // TCR-1522 Task 8 — chỉ ke_toan_truong có key này (xem
    // role-permissions.service.ts BE, TCR-1522 2026-09-06) — mục menu ẩn với
    // mọi role B2B khác (b2b_sales, b2b_sales_manager, accountant...).
    if (hasPermission("b2b.orders.credit_approve"))
      b2bChildren.push({
        key: ROUTES.b2b.creditApprovals,
        label: "Duyệt vượt hạn mức công nợ",
        onClick: () => onNavigate(ROUTES.b2b.creditApprovals),
      });
    // 5. Báo cáo (tổng hợp cuối)
    // TCR-1831 — 'b2b.reports.view' (quyền gộp) đã tách thành 4 key con theo tab
    // (executive/revenue/staff_kpi/rebate.view). Đổi nhãn từ "Báo cáo điều hành" sang "Báo
    // cáo B2B" vì trang giờ có thể chỉ hiện 3 tab con (NVKD B2B) mà không có tab điều hành —
    // gate bằng OR cả 4 key, khớp route guard ở App.tsx (Tabs bên trong tự lọc theo quyền).
    if (
      hasPermission([
        "b2b.reports.executive.view",
        "b2b.reports.revenue.view",
        "b2b.reports.staff_kpi.view",
        "b2b.reports.rebate.view",
      ])
    )
      b2bChildren.push({
        key: ROUTES.b2b.executiveDashboard,
        label: "Báo cáo B2B",
        onClick: () => onNavigate(ROUTES.b2b.executiveDashboard),
      });
    // 6. Cài đặt
    // TCR-1770 — đổi từ 'b2b.crm.view' (quyền legacy đã xoá, chỉ "tình cờ" gate đúng route này)
    // sang key BE THẬT enforce màn Cài đặt B2B ('b2b.settings.view', xem App.tsx cùng ticket).
    if (hasPermission("b2b.settings.view") || isManagerPlus) {
      b2bChildren.push({
        key: ROUTES.b2b.settings,
        label: "Cài đặt",
        onClick: () => onNavigate(ROUTES.b2b.settings),
      });
    }

    items.push({
      key: "b2b-group",
      icon: <ShopOutlined />,
      ...maybeWidePopup("CRM B2B", b2bChildren, "P"),
      drilldown: true,
      children: b2bChildren,
    } as SidebarMenuItem);
  }

  // ─── §5 Sản phẩm ─────────────────────────────────────
  if (hasPermission("products.view")) {
    const productChildren: SidebarMenuItems = [
      {
        key: ROUTES.products.list,
        label: "Danh sách sản phẩm",
        onClick: () => onNavigate(ROUTES.products.list),
      },
      ...(hasPermission("services.view")
        ? [
            {
              key: ROUTES.services.list,
              label: "Dịch vụ",
              onClick: () => onNavigate(ROUTES.services.list),
            },
          ]
        : []),
      // TCR-1699/1704 — 4 item dưới đây trước đây hiện KHÔNG ĐIỀU KIỆN cho mọi role có
      // 'products.view' (chỉ cần vào được nhóm "Sản phẩm"), nhưng route thật của chúng
      // (App.tsx) đòi permission key KHÁC — 'categories.view'/'brands.view'/'attributes.view'
      // (không tồn tại trong permission catalogue, chỉ owner qua wildcard '*' mới pass) và
      // 'products.update' cho Đơn vị tính — nên mọi role khác đều bấm vào là 403. Gate lại
      // đúng theo permission mà route thật đòi để menu không "thừa" (thừa = hiện nhưng bấm
      // vào lại bị chặn).
      // TCR-1845 — 'categories.view'/'brands.view'/'attributes.view' không tồn tại trong
      // permission catalogue thật (chỉ owner qua wildcard '*' pass), nên không role thường
      // nào bật được các key này dù được cấp products.create/products.update để tạo/sửa sản
      // phẩm — thứ mà 3 submenu này phục vụ trực tiếp (chọn danh mục/thương hiệu/thuộc tính
      // lúc tạo/sửa SP). Gate thêm bằng products.create/products.update (OR).
      // TCR-1845 (vòng retest 3) — gate giờ nằm ở '@/constants/productCatalogueGates' và
      // App.tsx dùng CHUNG constant đó. Vòng trước sửa mỗi menu này còn route vẫn gate cứng
      // bằng 3 key chết, nên menu hiện đủ mà bấm vào trang nào cũng 403. ĐỪNG chép lại
      // literal ở đây — có test chống trôi trong constants/__tests__/productCatalogueGates.
      ...(hasPermission(CATEGORY_GATE)
        ? [
            {
              key: ROUTES.products.categories.list,
              label: "Danh mục",
              onClick: () => onNavigate(ROUTES.products.categories.list),
            },
          ]
        : []),
      ...(hasPermission(BRAND_GATE)
        ? [
            {
              key: ROUTES.products.brands.list,
              label: labelWithTier("Thương hiệu", "S"),
              onClick: () => onNavigate(ROUTES.products.brands.list),
            },
          ]
        : []),
      ...(hasPermission(ATTRIBUTE_GATE)
        ? [
            {
              key: ROUTES.products.attributes.list,
              label: labelWithTier("Thuộc tính", "S"),
              onClick: () => onNavigate(ROUTES.products.attributes.list),
            },
          ]
        : []),
      ...(hasPermission(UNIT_GATE)
        ? [
            {
              key: ROUTES.products.units,
              label: "Đơn vị tính",
              onClick: () => onNavigate(ROUTES.products.units),
            },
          ]
        : []),
      ...(hasPermission("combos.view")
        ? [
            {
              key: ROUTES.products.combosFix,
              label: "Combo thật",
              onClick: () => onNavigate(ROUTES.products.combosFix),
            },
            {
              key: ROUTES.products.combos,
              label: "Combo ảo",
              onClick: () => onNavigate(ROUTES.products.combos),
            },
          ]
        : []),
    ];
    items.push({
      key: "products-group",
      icon: <AppstoreOutlined />,
      label: "Sản phẩm",
      drilldown: true,
      children: productChildren,
      popupClassName:
        countMenuEntries(productChildren) > MULTICOL_THRESHOLD
          ? "erp-menu-popup-wide"
          : undefined,
    } as SidebarMenuItem);
  }

  // ─── §6 Marketing ─────────────────────────────────────
  // TCR-1585 — trước đây gate nhầm vào 'products.view' (quyền xem sản phẩm, không liên quan gì
  // tới Marketing) nên role bỏ hết quyền Marketing (nhưng vẫn còn 'products.view' vì cần cho
  // việc khác, vd thủ kho) vẫn thấy menu Marketing dữ liệu trống. Gate đúng theo các key
  // marketing.* + loyalty.view mà 6 mục con thực sự dùng.
  //
  // TCR-1727 — gate ở CẤP NHÓM (OR 5 key) là chưa đủ: 5 mục con trước đây render VÔ ĐIỀU KIỆN
  // nên role chỉ có đúng 1 key trong nhóm (vd Thủ kho chỉ có 'loyalty.view') vẫn thấy đủ cả
  // Khuyến mãi/Voucher/Flash sale/Báo cáo KM. Mỗi mục con giờ gate theo đúng key mà route của
  // nó dùng ở BE. Nhóm hiện khi có ÍT NHẤT 1 mục con — không cần điều kiện OR riêng nữa.
  // Lưu ý: `SubNavBar` (tab strip ngang đầu trang) dẫn xuất siblings từ chính hàm này qua
  // `useActiveMenuGroup`, nên sửa ở đây cũng ẩn luôn các tab tương ứng — một nguồn sự thật.
  const marketingChildren: SidebarMenuItems = [
    ...(hasPermission("marketing.promotions.view")
      ? [
          {
            key: ROUTES.promotions.list,
            icon: <TagsOutlined />,
            label: labelWithTier("Khuyến mãi", "S"),
            onClick: () => onNavigate(ROUTES.promotions.list),
          },
        ]
      : []),
    ...(hasPermission("marketing.vouchers.view")
      ? [
          {
            key: ROUTES.promotions.vouchers,
            label: labelWithTier("Voucher", "S"),
            onClick: () => onNavigate(ROUTES.promotions.vouchers),
          },
        ]
      : []),
    ...(hasPermission("marketing.flash_sales.view")
      ? [
          {
            key: ROUTES.promotions.flashSales,
            label: labelWithTier("Flash sale", "P"),
            onClick: () => onNavigate(ROUTES.promotions.flashSales),
          },
        ]
      : []),
    // {
    // TCR-1243: trang wholesale-gifts đã tồn tại và có dữ liệu thật
    // nhưng trước đây không có mục điều hướng nào trong menu Marketing,
    // khiến tính năng "mồ côi" — chỉ vào được bằng cách gõ thẳng URL.
    //     key: ROUTES.promotions.wholesaleGifts,
    //     label: labelWithTier('Quà tặng sỉ', 'P'),
    //     onClick: () => onNavigate(ROUTES.promotions.wholesaleGifts),
    // },
    ...(hasPermission("loyalty.view")
      ? [
          {
            key: ROUTES.promotions.loyalty,
            icon: <TrophyOutlined />,
            label: labelWithTier("Tích điểm", "S"),
            onClick: () => onNavigate(ROUTES.promotions.loyalty),
          },
        ]
      : []),
    ...(hasPermission("marketing.reports.view")
      ? [
          {
            key: ROUTES.promotions.reports,
            icon: <LineChartOutlined />,
            label: labelWithTier("Báo cáo KM", "S"),
            onClick: () => onNavigate(ROUTES.promotions.reports),
          },
        ]
      : []),
  ];

  if (marketingChildren.length > 0) {
    items.push({
      key: "marketing-group",
      icon: <TagsOutlined />,
      label: "Marketing",
      drilldown: true,
      children: marketingChildren,
    } as SidebarMenuItem);
  }

  // ─── §4 Kho hàng ──────────────────────────────────────────────────────────
  // RBAC fix TCR (2026-09-02, Task F1) — legacy pre-redesign key 'inventory.view' đổi thành
  // 'inventory.stock.view' (catalogue hiện hành, xem role-seed.service.ts). Trước fix này,
  // nv_kho (và bất kỳ role mới nào không có 5 key legacy) bị khoá HOÀN TOÀN khỏi cả mục Kho
  // hàng dù BE đã cấp đúng bộ key hiện hành — chỉ owner/manager/inventory (Thủ kho) qua được
  // nhờ wildcard 'inventory.*' (tự khớp CẢ key cũ lẫn key mới, không phụ thuộc literal key nào
  // ở đây). Xem đầy đủ bảng mapping tại task-F1-report.md.
  if (hasPermission("inventory.stock.view")) {
    const invChildren: SidebarMenuItems = [
      {
        key: ROUTES.inventory.list,
        label: "Tồn kho",
        onClick: () => onNavigate(ROUTES.inventory.list),
      },
      // 'inventory.warehouse.view' — key MỚI hoàn toàn, KHÔNG tồn tại trước fix này (mục
      // "Quản lý kho" trước đây không có gate riêng, hiển thị bất cứ khi nào qua được gate
      // ngoài 'inventory.view'). Thêm gate riêng ở đây để KHÔNG nới quyền: nv_kho/
      // nv_mua_hang có 'inventory.stock.view' nhưng CỐ Ý KHÔNG có 'inventory.warehouse.view'
      // (Excel không cấp — xem role-permissions.service.ts comment tại khối nv_kho), nếu để
      // mục này ăn theo gate ngoài thì 2 role đó sẽ thấy menu dẫn tới route mà API chắc chắn
      // 403 (route 'warehouses'/'warehouses/new' đã đổi sang đòi đúng key này, xem App.tsx).
      ...(hasPermission("inventory.warehouse.view")
        ? [
            {
              key: ROUTES.inventory.warehouses,
              label: "Quản lý kho",
              onClick: () => onNavigate(ROUTES.inventory.warehouses),
            },
          ]
        : []),
      ...(hasPermission("inventory.location.view")
        ? [
            {
              key: ROUTES.inventory.warehouseLocations,
              label: "Vị trí kho",
              onClick: () => onNavigate(ROUTES.inventory.warehouseLocations),
            },
          ]
        : []),
      ...(hasPermission("inventory.lot.view")
        ? [
            {
              key: ROUTES.inventory.lots,
              label: "Lô / HSD",
              onClick: () => onNavigate(ROUTES.inventory.lots),
            },
            {
              key: ROUTES.inventory.recalls,
              label: "Thu hồi lô hàng",
              onClick: () => onNavigate(ROUTES.inventory.recalls),
            },
          ]
        : []),
      // Task F1 (2026-09-02) — 3 mục dưới đây trước fix hiển thị KHÔNG ĐIỀU KIỆN (chỉ ăn
      // theo gate ngoài 'inventory.view'/nay 'inventory.stock.view'). Route-level guard ở
      // App.tsx đã đổi sang key cụ thể hơn cho từng trang (movements.view/
      // reorder_alert.view/costing.view — đều tồn tại trong catalogue và KHÔNG phải wildcard
      // của 'inventory.stock.view'), nên phải thêm gate riêng ở đây tương ứng — nếu không,
      // role chỉ có 'inventory.stock.view' (vd nv_mua_hang) sẽ thấy các mục này trên menu
      // nhưng bấm vào bị 403 (route đòi key cụ thể mà role đó không có).
      ...(hasPermission("inventory.reorder_alert.view")
        ? [
            {
              key: ROUTES.inventory.alerts,
              label: "Cảnh báo tồn",
              onClick: () => onNavigate(ROUTES.inventory.alerts),
            },
          ]
        : []),
      ...(hasPermission("inventory.movements.view")
        ? [
            {
              key: ROUTES.inventory.movements,
              label: "Lịch sử biến động",
              onClick: () => onNavigate(ROUTES.inventory.movements),
            },
          ]
        : []),
      ...(hasPermission("inventory.costing.view")
        ? [
            {
              key: ROUTES.inventory.costing,
              label: "Tính giá xuất kho",
              onClick: () => onNavigate(ROUTES.inventory.costing),
            },
            {
              key: ROUTES.inventory.costExceptions,
              label: "Ngoại lệ giá vốn",
              onClick: () => onNavigate(ROUTES.inventory.costExceptions),
            },
          ]
        : []),
    ];
    // RBAC fix TCR (2026-09-02, Task F1) — 'inventory.stock_in' (legacy, gác CẢ 2 mục "Phiếu
    // nhập" lẫn "Phiếu xuất" bằng 1 key duy nhất) tách đúng theo catalogue hiện hành:
    // 'inventory.receipts.manage' cho Phiếu nhập, 'inventory.issues.manage' cho Phiếu xuất —
    // khớp đúng route-level guard mới ở App.tsx (không còn dùng chung 1 key cho 2 route khác
    // nhau). nv_kho có CẢ 2 key nên vẫn thấy đủ 2 mục như hành vi cũ (không mất quyền).
    if (
      hasPermission("inventory.receipts.manage") ||
      hasPermission("inventory.issues.manage")
    ) {
      invChildren.push({ type: "divider" as const });
      if (hasPermission("inventory.receipts.manage")) {
        invChildren.push({
          key: ROUTES.inventory.receipts,
          label: "Phiếu nhập",
          onClick: () => onNavigate(ROUTES.inventory.receipts),
        });
      }
      if (hasPermission("inventory.issues.manage")) {
        invChildren.push({
          key: ROUTES.inventory.issues,
          label: "Phiếu xuất",
          onClick: () => onNavigate(ROUTES.inventory.issues),
        });
      }
    }
    // TCR-1517 Task 7 — Lệnh lắp ráp / tháo dỡ combo thật (nhóm "Combo thật",
    // route-level guard ở App.tsx đòi đúng 'inventory.assembly.view').
    if (hasPermission("inventory.assembly.view")) {
      invChildren.push({
        key: ROUTES.inventory.assemblyOrders,
        label: "Lệnh lắp ráp",
        onClick: () => onNavigate(ROUTES.inventory.assemblyOrders),
      });
    }
    // legacy 'inventory.transfer' → 'inventory.transfers.manage'
    if (hasPermission("inventory.transfers.manage")) {
      invChildren.push({
        key: ROUTES.inventory.transfers,
        label: labelWithTier("Chuyển kho", "P"),
        onClick: () => onNavigate(ROUTES.inventory.transfers),
      });
    }
    // legacy 'inventory.adjust' → 'inventory.counts.manage'
    if (hasPermission("inventory.counts.manage")) {
      invChildren.push({
        key: ROUTES.inventory.counts,
        label: "Kiểm kê",
        onClick: () => onNavigate(ROUTES.inventory.counts),
      });
    }
    /**
     * TCR-804 — hai báo cáo kho chuyển từ mục Kế toán › Báo cáo về đây.
     *
     * Trang và route KHÔNG đổi (`/reports/...`, `pages/AccountingReports/`),
     * chỉ đổi chỗ đứng trong menu. Đặt CUỐI CÙNG của mục Kho hàng — sau cả
     * nhóm chứng từ (Phiếu nhập, Phiếu xuất, Chuyển kho, Kiểm kê). Phải push
     * ở đây, sau các khối `if` quyền, vì nếu để trong mảng khởi tạo thì mọi
     * mục push sau đó sẽ rơi xuống dưới chúng.
     *
     * "Báo cáo tồn" đã bỏ khỏi menu theo cùng ticket: "Tổng hợp nhập xuất
     * tồn" đã chi tiết hơn hẳn. Hằng `ROUTES.inventory.reports` cũng đã
     * xoá, nhưng TRANG THÌ KHÔNG chết: `App.tsx` khai route đó bằng đường
     * dẫn thẳng (`path="reports"` dưới nhánh `/inventory`), nên link cũ
     * vẫn mở được — chỉ rút khỏi menu.
     *
     * RBAC fix (2026-09-02, whole-branch review fix round, thay thế đoạn comment cũ nói
     * "theo inventory.view của mục này" — key đó đã đổi tên thành 'inventory.stock.view' và
     * KHÔNG còn là gate của 2 mục dưới đây nữa, xem lý do bên dưới) — 2 mục này KHÔNG ăn theo
     * gate mở nhóm ('inventory.stock.view') như trước Task F1, vì route thật (App.tsx) đòi
     * key hẹp hơn: "Tổng hợp nhập xuất tồn" đòi `accounting.reports` HOẶC
     * `inventory.reports.view`, "Bảng kê phiếu kho" đòi ĐÚNG `accounting.reports` (route cũ
     * đã đổi PermissionsGuard, không còn giữ `@Roles('viewer','manager','owner')` — role
     * `viewer` cũng đã bị xoá khỏi hệ thống 2026-08-30). F1 nới gate nhóm từ
     * 'inventory.view' cũ sang 'inventory.stock.view' khiến 3 role thêm
     * (nv_mua_hang/ecom_sales_manager/nv_sale_ecom — có 'inventory.stock.view' nhưng KHÔNG có
     * `accounting.reports` lẫn `inventory.reports.view`) lọt vào nhóm và thấy 2 mục này dù
     * bấm vào sẽ 403 — gate lại đúng theo route thật ở đây để không còn dead link.
     */
    if (
      hasPermission("accounting.reports") ||
      hasPermission("inventory.reports.view")
    ) {
      invChildren.push({
        key: ROUTES.accounting.reportInventorySummary,
        label: "Tổng hợp nhập xuất tồn",
        onClick: () => onNavigate(ROUTES.accounting.reportInventorySummary),
      });
    }
    if (hasPermission("accounting.reports")) {
      invChildren.push({
        key: ROUTES.accounting.reportWarehouseVoucher,
        label: "Bảng kê phiếu kho",
        onClick: () => onNavigate(ROUTES.accounting.reportWarehouseVoucher),
      });
    }
    items.push({
      key: "inventory-group",
      icon: <InboxOutlined />,
      label: "Kho hàng",
      drilldown: true,
      children: invChildren,
      popupClassName:
        countMenuEntries(invChildren) > MULTICOL_THRESHOLD
          ? "erp-menu-popup-wide"
          : undefined,
    } as SidebarMenuItem);
  }

  // ─── §6 Giao vận ──────────────────────────────────────────────────────────
  // RBAC fix (2026-09-02, whole-branch review fix round) — key cũ 'shipping.view' đã bị BE
  // (Task B2.3) XOÁ HẲN khỏi catalogue, tách thành 4 key riêng theo đúng 4 dòng Excel/4 màn
  // hình con: 'shipping.orders.view' (Đơn giao vận), 'shipping.cod_reconciliation.view' (Đối
  // soát COD), 'shipping.pickup_locations.view'/'.manage' (Nơi lấy hàng),
  // 'shipping.logs.view' (Logs vận chuyển). Gate nhóm ở đây dùng đúng 'shipping.orders.view' —
  // đây là dòng RỘNG NHẤT trong 4 dòng (mọi role có bất kỳ quyền Giao vận nào — inventory/
  // nv_kho/shipper/manager — đều có Đọc "Đơn giao vận", xem role-permissions.service.ts) nên
  // dùng làm điều kiện MỞ nhóm là an toàn.
  //
  // Nhưng 4 mục con KHÔNG còn dùng chung 1 key như trước (khi 'shipping.view' còn gộp) — nếu cứ
  // hiện cả 4 mục con vô điều kiện một khi nhóm đã mở thì role chỉ có 'shipping.orders.view'
  // (inventory/nv_kho, theo Excel CHỈ Đọc đúng dòng "Đơn giao vận") sẽ thấy thêm 3 link "Đối
  // soát COD"/"Nơi lấy hàng"/"Logs vận chuyển" trên menu nhưng bấm vào bị 403 — đúng lớp bug
  // "dead menu link" mà cả đợt fix này đang dọn (F1 Kho hàng cùng lớp). Nên mỗi mục con dưới
  // đây gate riêng đúng key route thật của nó (mirror App.tsx) thay vì ăn theo gate ngoài.
  if (hasPermission("shipping.orders.view")) {
    const shippingChildren: SidebarMenuItems = [
      {
        key: ROUTES.shipping.list,
        label: "Đơn giao vận",
        onClick: () => onNavigate(ROUTES.shipping.list),
      },
    ];
    if (hasPermission("shipping.cod_reconciliation.view")) {
      shippingChildren.push({
        // Ngay dưới "Đơn giao vận": đối soát là bước tiếp theo của
        // chính những vận đơn ở trên, không phải việc cấu hình.
        key: ROUTES.shipping.codReconciliation,
        label: "Đối soát COD",
        onClick: () => onNavigate(ROUTES.shipping.codReconciliation),
      });
    }
    if (hasPermission("shipping.cod.manage")) {
      shippingChildren.push({
        // Tách khỏi "Đối soát COD" ngay trên: kia là đối soát với ĐVVC qua file
        // hãng gửi, đây là tiền mặt người giao của TCR nộp lại (TCR-1521).
        key: ROUTES.shipping.codRemittances,
        label: "Nộp tiền thu hộ",
        onClick: () => onNavigate(ROUTES.shipping.codRemittances),
      });
    }
    // RBAC fix (2026-09-02, whole-branch review — 2 lượt). Lượt 1 từng gate mục này bằng
    // '.manage' (lý do lúc đó: trang có nút Thêm/Sửa vô điều kiện, route ghi BE đòi .manage)
    // nhưng SAI — verify lại role-permissions.service.ts khối `shipper` xác nhận shipper CHỈ
    // giữ đúng 'shipping.pickup_locations.VIEW' ("giữ NGUYÊN đủ cả 4 dòng, không narrowing"),
    // và route GET (list) trên BE chỉ đòi '.view' — CHỈ 2 route ghi (add/update) mới đòi
    // '.manage'. Gate menu bằng '.manage' khiến shipper mất hẳn link này dù có quyền Đọc thật.
    // Lượt 2: gate menu (và route ở App.tsx) lại bằng '.view' — khớp đúng route GET + không
    // làm mất quyền đọc của shipper. Việc tách đọc/ghi chuyển xuống ĐÚNG tầng UI bên trong
    // trang (nút Thêm/Sửa chỉ hiện với `.manage`, xem pickup-locations.tsx) thay vì chặn ở
    // route/menu.
    if (hasPermission("shipping.pickup_locations.view")) {
      shippingChildren.push({
        key: ROUTES.shipping.pickupLocations,
        label: "Nơi lấy hàng",
        onClick: () => onNavigate(ROUTES.shipping.pickupLocations),
      });
    }
    if (hasPermission("shipping.logs.view")) {
      shippingChildren.push({
        // Trang gộp nhật ký API của mọi DVVC. Route cũ
        // /shipping/ghtk-logs vẫn chạy được nhưng cố ý ẩn khỏi menu.
        key: ROUTES.shipping.logs,
        label: "Logs vận chuyển",
        onClick: () => onNavigate(ROUTES.shipping.logs),
      });
    }
    items.push({
      key: "shipping-group",
      icon: <CarOutlined />,
      label: labelWithTier("Giao vận", "P"),
      drilldown: true,
      children: shippingChildren,
    } as SidebarMenuItem);
  }

  // ─── §10 Khách hàng & NCC ────────────────────────────────────────────────
  // Gom: Khách hàng, CRM, NCC, Đặt hàng, Hỗ trợ.
  if (
    hasPermission("customers.view") ||
    hasPermission("customers.groups.view") ||
    hasPermission("suppliers.view")
  ) {
    const partnersChildren: SidebarMenuItems = [];
    if (hasPermission("customers.view")) {
      partnersChildren.push({
        key: ROUTES.customers.list,
        icon: <ContactsOutlined />,
        label: "Khách hàng",
        onClick: () => onNavigate(ROUTES.customers.list),
      });
    }
    // TCR-1747 — quyền xem nhóm tách khỏi `customers.view`: thủ kho cần tra cứu
    // khách hàng trong các form nghiệp vụ nhưng không được nhìn thấy màn Nhóm KH.
    if (hasPermission("customers.groups.view")) {
      partnersChildren.push({
        key: ROUTES.customers.groups,
        icon: <TeamOutlined />,
        label: "Nhóm khách hàng",
        onClick: () => onNavigate(ROUTES.customers.groups),
      });
    }
    // RBAC fix (TCR-1600) — "CRM Follow-up"/"CRM Dashboard" trước đây ăn theo gate ngoài
    // 'customers.view' (không có gate riêng), trong khi API thật (crm.controller.ts) đòi đúng
    // 'crm.view' — role có customers.view nhưng KHÔNG có crm.view (vd Thủ kho trên tenant chưa
    // được backfill key này) vẫn thấy 2 mục này dù bấm vào các thao tác sẽ 403. Gate riêng theo
    // đúng permission route thật yêu cầu, độc lập với customers.view.
    if (hasPermission("crm.view")) {
      partnersChildren.push({
        key: ROUTES.crm.followUps,
        label: labelWithTier("CRM Follow-up", "P"),
        onClick: () => onNavigate(ROUTES.crm.followUps),
      });
      partnersChildren.push({
        key: ROUTES.crm.dashboard,
        label: labelWithTier("CRM Dashboard", "P"),
        onClick: () => onNavigate(ROUTES.crm.dashboard),
      });
    }
    // TCR-1757 — QA yêu cầu ẩn HẲN menu "Chương trình giới thiệu" (không phải ẩn có điều
    // kiện): tính năng referral không được dùng trên web (cùng lý do TCR-1736 đã ẩn hẳn
    // nhóm quyền referral khỏi màn Vai trò & Quyền — xem HIDDEN_PERMISSION_KEYS ở
    // role-seed.service.ts). Thử nghiệm trước đó (TCR-1757 fix vòng 1) gate menu theo
    // `is_program_active` — QA vẫn thấy menu vì owner có wildcard '*' luôn qua được điều
    // kiện permission, và bản chất yêu cầu là ẩn vĩnh viễn chứ không phải theo trạng thái
    // bật/tắt chương trình. Xoá hẳn khối push menu này — không còn điều kiện nào hiện lại.
    if (hasPermission("suppliers.view")) {
      partnersChildren.push({ type: "divider" as const });
      partnersChildren.push({
        key: ROUTES.suppliers.list,
        label: "Nhà cung cấp",
        onClick: () => onNavigate(ROUTES.suppliers.list),
      });
    }

    items.push({
      key: "partners-group",
      icon: <TeamOutlined />,
      label: "Khách hàng & NCC",
      drilldown: true,
      children: partnersChildren,
      popupClassName:
        countMenuEntries(partnersChildren) > MULTICOL_THRESHOLD
          ? "erp-menu-popup-wide"
          : undefined,
    } as SidebarMenuItem);
  }

  // ─── §13 Báo cáo (ẩn khỏi sidebar; route vẫn được giữ) ─────────────────

  // ─── §13b Tài chính ──────────────────────────────────────────────────
  // RBAC fix (2026-09-02, Task F2.2) — 'finance.view' XOÁ HẲN: grep toàn bộ BE (catalogue lẫn
  // role-permissions.service.ts) xác nhận key này KHÔNG tồn tại ở BẤT KỲ đâu — không role hệ
  // thống nào, không cả seed catalogue cho custom role, tức là orphaned hoàn toàn (không ai bấm
  // được checkbox này trong Settings > Vai trò vì nó không hiển thị). 'reports.revenue'/
  // 'reports.inventory' GIỮ NGUYÊN dù cũng không role hệ thống nào đang giữ — 2 key này VẪN có
  // trong catalogue seed (role-seed.service.ts, group 'reports'), tức tenant admin có thể đã tạo
  // custom role và tick chọn 2 quyền này qua UI Vai trò — xoá sẽ âm thầm phá quyền của custom
  // role đó. Trước fix này, group "Tài chính" (COGS/EOD/HĐĐT) THỰC TẾ chỉ hiện cho owner
  // (wildcard '*') vì cả 3 key cũ đều KHÔNG được gán cho bất kỳ role hệ thống nào khác — thêm
  // 'accounting.reports' (role-seed label đúng nghĩa "Xem báo cáo kế toán/tài chính", đã cấp cho
  // manager/accountant/ke_toan_truong từ trước B3) làm gate chính khớp đúng tên nhóm "Tài chính".
  // AMIS đã có menu riêng trong nhóm Kế toán/Accounting; nhóm Tài chính không dùng quyền AMIS
  // để mở nữa.
  // COGS ('reports/profit')/HĐĐT ('finance/einvoices') ở App.tsx hiện
  // KHÔNG có requiredPermissions riêng (chỉ FeatureGate theo tier) nên gate ở đây là lớp chặn
  // DUY NHẤT phía FE cho nhóm này.
  //
  // RBAC fix (2026-09-02, whole-branch review — 2 lượt) — EOD ('reports/eod'):
  // Lượt 1: route App.tsx trước đó tự khoá riêng bằng RoleGuard(['owner','manager']), không phụ
  // thuộc gate group này — đổi RoleGuard sang requiredPermissions=['accounting.reports'] (khớp
  // key đang mở group) vì 'ke_toan_truong' không nằm trong union type RoleGuard chấp nhận.
  // Lượt 2 (SAI Ở LƯỢT 1, sửa lại): route thật đứng sau màn EOD
  // (`eod-report/controllers/eod.controller.ts`, `@Controller('eod')`, xem comment đầy đủ tại
  // route trong App.tsx) đòi ĐÚNG 'reports.eod.view' (READ)/'reports.eod.manage' (WRITE) — KHÔNG
  // phải 'accounting.reports'. gd_kd (Giám đốc KD) giữ 'reports.eod.view' nhưng KHÔNG giữ
  // 'accounting.reports' nên route requiredPermissions=['accounting.reports'] ở Lượt 1 vẫn
  // dead-end gd_kd dù group này họ vẫn mở được (qua 'reports.revenue'/COGS họ có sẵn — không
  // phải qua EOD). Sửa route App.tsx sang 'reports.eod.view', và thêm gate RIÊNG cho item con
  // này ở đây (trước đó EOD KHÔNG có gate riêng, chỉ ăn theo group) để mirror đúng route — mọi
  // role hiện có 'reports.eod.view' đều đã có 'reports.revenue' kèm theo (xem
  // role-permissions.service.ts, 2 key này luôn được gán cùng dòng) nên hôm nay đây là gate
  // PHÒNG THỦ (chưa có role nào rơi vào ca lệch), không phải fix hành vi hiện tại.
  if (
    hasPermission("reports.revenue") ||
    hasPermission("reports.inventory") ||
    hasPermission("accounting.reports")
  ) {
    const financeChildren: SidebarMenuItems = [
      {
        key: ROUTES.reportsAdvanced.profit,
        label: labelWithTier("Lợi nhuận (COGS)", "P"),
        onClick: () => onNavigate(ROUTES.reportsAdvanced.profit),
      },
      ...(hasPermission("reports.eod.view")
        ? [
            {
              key: ROUTES.reports.eod,
              icon: <MoneyCollectOutlined />,
              label: "Cuối ngày (EOD)",
              onClick: () => onNavigate(ROUTES.reports.eod),
            },
          ]
        : []),
      {
        key: ROUTES.finance.einvoiceList,
        label: labelWithTier("Hoá đơn điện tử", "S"),
        onClick: () => onNavigate(ROUTES.finance.einvoiceList),
      },
    ];

    items.push({
      key: "finance-group",
      icon: <BankOutlined />,
      label: "Tài chính",
      drilldown: true,
      children: financeChildren,
      popupClassName:
        countMenuEntries(financeChildren) > MULTICOL_THRESHOLD
          ? "erp-menu-popup-wide"
          : undefined,
    } as SidebarMenuItem);
  }

  // ─── §14 Kế toán — INDEPENDENT block (NOT inside reports if) ─────────────
  // Full menu theo feature-screens.md. Sections are nested to keep the accounting popup compact.
  //
  // Quyền Mua hàng (`purchasing.orders.*`) nằm trong phân hệ Kế toán ở tầng hiển thị
  // (PermissionsPicker gom group `purchasing` vào cột Kế toán), nhưng matcher RBAC
  // KHÔNG coi `purchasing.*` là `accounting.*`. Nếu gate ngoài chỉ hỏi accounting.*
  // thì role nv_mua_hang / custom role chỉ có purchasing.orders.view|manage sẽ
  // không bao giờ thấy group "Kế toán" — mục "Mua hàng" bên trong không có cơ hội hiện.
  // `.manage` cũng không tự bao `.view` (hasRuntimePermission exact/prefix only).
  const canAccessPurchaseOrders = hasPermission([
    "purchasing.orders.view",
    "purchasing.orders.manage",
  ]);
  if (
    hasPermission("accounting.reports") ||
    hasPermission("accounting.vouchers") ||
    hasPermission("accounting.settings") ||
    // BE đổi tên key quyền ĐỌC HĐĐT: `accounting.einvoice` (bare) → `accounting.einvoice.view`.
    // Gate group phải theo key MỚI y hệt các mục con, nếu không thì role chỉ có
    // `.view` sẽ không bao giờ thấy group "Kế toán" và mất sạch các mục HĐĐT bên trong.
    hasPermission("accounting.einvoice.view") ||
    hasPermission("accounting.inventory") ||
    hasPermission("accounting.ar") ||
    hasPermission("accounting.debt_settlements.view") ||
    hasPermission("accounting.debt_settlements.manage") ||
    hasPermission("accounting.settings.repair") ||
    canAccessPurchaseOrders
  ) {
    const acctChildren: SidebarMenuItems = [];
    const pushAcctSection = (
      key: string,
      label: string,
      children: SidebarMenuItems,
    ) => {
      if (children.length === 0) return;
      acctChildren.push({
        key,
        label: accountingSectionLabel(key, label),
        children,
        popupClassName: "erp-menu-popup-accounting-section",
      });
    };

    // Route `/accounting/dashboard` đòi `accounting.reports`. Không gắn theo
    // gate ngoài — role chỉ có purchasing.* sẽ thấy mục này rồi 403.
    if (hasPermission("accounting.reports")) {
      pushAcctSection("accounting-section-overview", "Tổng quan", [
        {
          key: ROUTES.accounting.dashboard,
          label: "Tổng quan kế toán",
          onClick: () => onNavigate(ROUTES.accounting.dashboard),
        },
      ]);
    }

    // Bán hàng kế toán
    if (hasPermission("accounting.vouchers")) {
      const salesAccountingChildren: SidebarMenuItems = [
        {
          key: ROUTES.accounting.salesDocuments,
          label: "Chứng từ bán hàng",
          onClick: () => onNavigate(ROUTES.accounting.salesDocuments),
        },
      ];
      salesAccountingChildren.push({
        key: ROUTES.accounting.giftOrders,
        label: "Đơn hàng có Gift",
        onClick: () => onNavigate(ROUTES.accounting.giftOrders),
      });
      pushAcctSection("accounting-section-sales", "Bán hàng", salesAccountingChildren);
    }

    // Mua hàng kế toán
    const purchaseAccountingChildren: SidebarMenuItems = [];
    if (canAccessPurchaseOrders) {
      purchaseAccountingChildren.push({
        key: ROUTES.purchasing.list + "__acct",
        label: "Đơn đặt hàng",
        onClick: () => onNavigate(ROUTES.purchasing.list),
      });
    }
    // TCR-1995 — "Nhận hàng mua" tách riêng khỏi `canAccessPurchaseOrders` (OR view/manage):
    // route đích (receiving-list.tsx → PATCH/POST /purchase-orders/:id/receive) đòi tường minh
    // `purchasing.orders.manage` (purchase-order.controller.ts) — `.view` không tự bao gồm
    // `.manage` (hasGrantedPermission() không có ngữ nghĩa "manage tự include view" hay ngược
    // lại, cùng pattern đã ghi ở comment `canAccessPurchaseOrders` phía trên). Thủ kho/NV Kho
    // (và mọi role .view-only khác — accountant/ke_toan_truong/nv_mua_hang) trước đây thấy mục
    // menu này dù không nhận hàng được — thuần thiếu gate ở tầng hiển thị, backend đã đúng
    // từ đầu.
    if (hasPermission("purchasing.orders.manage")) {
      purchaseAccountingChildren.push({
        key: ROUTES.purchasing.receivingList,
        label: "Nhận hàng mua",
        onClick: () => onNavigate(ROUTES.purchasing.receivingList),
      });
    }
    if (hasPermission("accounting.vouchers")) {
      purchaseAccountingChildren.push(
        {
          key: ROUTES.accounting.purchaseInvoices,
          label: "Chứng từ mua hàng/dịch vụ",
          onClick: () => onNavigate(ROUTES.accounting.purchaseInvoices),
        },
        {
          key: ROUTES.accounting.purchaseReturns,
          label: "Trả hàng mua",
          onClick: () => onNavigate(ROUTES.accounting.purchaseReturns),
        },
        {
          key: ROUTES.accounting.purchaseDiscounts,
          label: "Giảm giá hàng mua",
          onClick: () => onNavigate(ROUTES.accounting.purchaseDiscounts),
        },
      );
    }
    pushAcctSection(
      "accounting-section-purchase",
      "Mua hàng",
      purchaseAccountingChildren,
    );

    // Hóa đơn GTGT
    // Gate theo `accounting.einvoice.view` — BE đổi tên key quyền ĐỌC từ
    // `accounting.einvoice` (bare) sang `accounting.einvoice.view`.
    const invoiceChildren: SidebarMenuItems = [];
    if (hasPermission("accounting.einvoice.view")) {
      invoiceChildren.push({
        key: ROUTES.accounting.accountingEInvoices,
        label: "Hóa đơn GTGT",
        onClick: () => onNavigate(ROUTES.accounting.accountingEInvoices),
      });
    }
    if (hasPermission("accounting.einvoice.view")) {
      invoiceChildren.push({
        key: ROUTES.accounting.accountingEInvoices + "/settings",
        label: "Cài đặt HĐĐT",
        onClick: () =>
          onNavigate(ROUTES.accounting.accountingEInvoices + "/settings"),
      });
    }
    /**
     * TCR-810 — "Bảng kê hoá đơn" chuyển từ mục Báo cáo sang mục Hoá đơn.
     *
     * Vị trí theo ảnh đính kèm của ticket: ngay sau "Cài đặt HĐĐT", trước
     * "Hoá đơn đầu vào". Trang và route KHÔNG đổi — bản thân báo cáo sẽ do
     * anh Nhân sửa sau, ticket này chỉ đổi chỗ đứng trong menu.
     *
     * Quyền: giữ `accounting.reports` VÀ nhận thêm `accounting.einvoice.view`.
     * Nếu chỉ gán theo `accounting.einvoice.view` như các mục còn lại của nhóm
     * thì người chỉ có quyền báo cáo sẽ MẤT mục này — di chuyển menu không
     * được phép thu hẹp quyền truy cập. An toàn ở tầng API: endpoint
     * `GET /accounting/reports/invoice-listing` chỉ chặn theo VAI TRÒ
     * (`@Roles('viewer','manager','owner')`), không đòi quyền nào ở trên.
     */
    if (
      hasPermission("accounting.einvoice.view") ||
      hasPermission("accounting.reports")
    ) {
      invoiceChildren.push({
        key: ROUTES.accounting.reportInvoiceListing,
        label: "Bảng kê hoá đơn",
        onClick: () => onNavigate(ROUTES.accounting.reportInvoiceListing),
      });
    }
    if (hasPermission("accounting.einvoice.view")) {
      invoiceChildren.push({
        key: ROUTES.accounting.govInvoices,
        label: "Hóa đơn đầu vào",
        onClick: () => onNavigate(ROUTES.accounting.govInvoices),
      });
    }
    pushAcctSection("accounting-section-invoices", "Hóa đơn", invoiceChildren);

    // Tiền mặt, ngân hàng
    if (hasPermission("accounting.vouchers")) {
      pushAcctSection("accounting-section-cash-bank", "Tiền mặt, ngân hàng", [
        {
          key: ROUTES.accounting.cash,
          label: "Tiền mặt",
          onClick: () => onNavigate(ROUTES.accounting.cash),
        },
        {
          key: ROUTES.accounting.bankDeposits,
          label: "Tiền gửi",
          onClick: () => onNavigate(ROUTES.accounting.bankDeposits),
        },
        {
          key: ROUTES.accounting.transactions,
          label: "Tất cả thu chi",
          onClick: () => onNavigate(ROUTES.accounting.transactions),
        },
      ]);
    }

    // Tài sản & phân bổ
    if (hasPermission("accounting.vouchers")) {
      pushAcctSection("accounting-section-assets", "Tài sản & phân bổ", [
        {
          key: ROUTES.accounting.prepaidExpenses,
          label: "Chi phí trả trước",
          onClick: () => onNavigate(ROUTES.accounting.prepaidExpenses),
        },
        {
          key: ROUTES.accounting.fixedAssets,
          label: "Tài sản cố định",
          onClick: () => onNavigate(ROUTES.accounting.fixedAssets),
        },
        {
          key: ROUTES.accounting.toolSupplies,
          label: "Công cụ dụng cụ",
          onClick: () => onNavigate(ROUTES.accounting.toolSupplies),
        },
        {
          key: ROUTES.accounting.toolSupplyReports,
          label: "Báo cáo CCDC",
          onClick: () => onNavigate(ROUTES.accounting.toolSupplyReports),
        },
        {
          key: ROUTES.accounting.otherDocument,
          label: "Chứng từ nghiệp vụ khác",
          onClick: () => onNavigate(ROUTES.accounting.otherDocument),
        },
      ]);
    }

    // Công nợ
    const debtChildren: SidebarMenuItems = [];
    if (hasPermission("accounting.ar")) {
      debtChildren.push(
        {
          key: ROUTES.accounting.customersAR,
          label: "Công nợ khách hàng",
          onClick: () => onNavigate(ROUTES.accounting.customersAR),
        },
        {
          key: ROUTES.accounting.suppliersAP,
          label: "Công nợ nhà cung cấp",
          onClick: () => onNavigate(ROUTES.accounting.suppliersAP),
        },
      );
    }
    if (
      hasPermission("accounting.debt_settlements.view") ||
      hasPermission("accounting.debt_settlements.manage")
    ) {
      debtChildren.push(
        {
          key: ROUTES.accounting.debtSettlements.customerDocumentOffset,
          label: "Đối trừ chứng từ",
          onClick: () =>
            onNavigate(
              ROUTES.accounting.debtSettlements.customerDocumentOffset,
            ),
        },
        {
          key: ROUTES.accounting.debtSettlements.customerDocumentOffsetMulti,
          label: "Đối trừ chứng từ nhiều đối tượng",
          onClick: () =>
            onNavigate(
              ROUTES.accounting.debtSettlements.customerDocumentOffsetMulti,
            ),
        },
        {
          key: ROUTES.accounting.debtSettlements.customerDebtOffset,
          label: "Đối trừ công nợ",
          onClick: () =>
            onNavigate(ROUTES.accounting.debtSettlements.customerDebtOffset),
        },
        {
          key: ROUTES.accounting.debtSettlements.removeCustomerDocumentOffset,
          label: "Bỏ đối trừ",
          onClick: () =>
            onNavigate(
              ROUTES.accounting.debtSettlements.removeCustomerDocumentOffset,
            ),
        },
      );
    }
    pushAcctSection("accounting-section-debt", "Công nợ", debtChildren);

    /**
     * Báo cáo kế toán
     *
     * TCR-765 — bỏ 9 báo cáo khỏi danh sách (mô tả ticket ghi "8", nhưng ảnh
     * thứ ba đính kèm ngày 03/08 — sau khi mô tả được viết ngày 30/07 — khoanh
     * thêm "Công nợ phải thu/trả" mà không sửa lại con số):
     *
     *   Bảng cân đối kế toán      Bảng kê chứng từ
     *   Bảng cân đối phát sinh    Bảng kê phiếu thu chi
     *   S2b, S2c, S2d, S2e        Công nợ phải thu/trả
     *
     * CHỈ bỏ khỏi menu — route và trang giữ nguyên (giống cách làm ở TCR-804),
     * nên link cũ / bookmark vẫn mở được, và nếu kế toán cần lại thì chỉ việc
     * thêm dòng menu.
     */
    if (hasPermission("accounting.reports")) {
      pushAcctSection("accounting-section-reports", "Báo cáo", [
        {
          key: ROUTES.accounting.reports + "__acct",
          label: "Tổng quan báo cáo",
          onClick: () => onNavigate(ROUTES.accounting.reports),
        },
        {
          key: ROUTES.accounting.reportBusinessResults,
          label: "Kết quả kinh doanh",
          onClick: () => onNavigate(ROUTES.accounting.reportBusinessResults),
        },
        {
          key: ROUTES.accounting.reportCashBook,
          label: "Sổ kế toán chi tiết quỹ tiền mặt",
          onClick: () => onNavigate(ROUTES.accounting.reportCashBook),
        },
        {
          key: ROUTES.accounting.reportBankDepositBook,
          label: "Sổ tiền gửi ngân hàng",
          onClick: () => onNavigate(ROUTES.accounting.reportBankDepositBook),
        },
        ...(hasProfessional
          ? [
              {
                key: ROUTES.accounting.reportJournalEntries,
                label: "Bút toán",
                onClick: () =>
                  onNavigate(ROUTES.accounting.reportJournalEntries),
              },
            ]
          : []),
        // TCR-804: "Tổng hợp nhập xuất tồn" và "Bảng kê phiếu kho" đã
        // chuyển sang mục Kho hàng (§4). Đừng thêm lại ở đây.
        // TCR-810: "Bảng kê hoá đơn" đã chuyển sang mục Hoá đơn ở trên.
        // Đừng thêm lại ở đây.
        {
          key: ROUTES.accounting.reportCustomersAR,
          label: "Báo cáo công nợ KH",
          onClick: () => onNavigate(ROUTES.accounting.reportCustomersAR),
        },
        {
          key: ROUTES.accounting.reportArDetail,
          label: "Chi tiết công nợ phải thu",
          onClick: () => onNavigate(ROUTES.accounting.reportArDetail),
        },
        {
          key: ROUTES.purchasing.apSummary + "__acct_reports",
          label: "TH công nợ NCC (TK 331)",
          onClick: () => onNavigate(ROUTES.purchasing.apSummary),
        },
        {
          key: ROUTES.purchasing.apDetail + "__acct_reports",
          label: "Chi tiết công nợ phải trả NCC",
          onClick: () => onNavigate(ROUTES.purchasing.apDetail),
        },
        {
          key: ROUTES.purchasing.purchaseLedger + "__acct_reports",
          label: "Sổ chi tiết mua hàng",
          onClick: () => onNavigate(ROUTES.purchasing.purchaseLedger),
        },
        {
          key: ROUTES.accounting.reportSalesSummaryByProduct,
          label: "Tổng hợp bán hàng theo mặt hàng",
          onClick: () =>
            onNavigate(ROUTES.accounting.reportSalesSummaryByProduct),
        },
        {
          key: ROUTES.accounting.reportSalesDetail,
          label: "Sổ chi tiết bán hàng",
          onClick: () => onNavigate(ROUTES.accounting.reportSalesDetail),
        },
      ]);
    }

    // Đồng bộ AMIS (kế toán)
    if (hasPermission("accounting.settings") || isManagerPlus) {
      pushAcctSection("accounting-section-amis", "Đồng bộ AMIS", [
        {
          // Gộp "Kết nối" + "Tác vụ" vào một mục; menu con nằm trong trang.
          key: ROUTES.accounting.amisConnect,
          label: "Cài đặt",
          onClick: () => onNavigate(ROUTES.accounting.amisConnect),
        },
        {
          key: ROUTES.accounting.amisOrgUnits,
          label: "Đơn vị kinh doanh",
          onClick: () => onNavigate(ROUTES.accounting.amisOrgUnits),
        },
        {
          key: ROUTES.accounting.amisStocks,
          label: "Kho",
          onClick: () => onNavigate(ROUTES.accounting.amisStocks),
        },
        {
          key: ROUTES.accounting.amisInventoryItems,
          label: "Vật tư hàng hoá",
          onClick: () => onNavigate(ROUTES.accounting.amisInventoryItems),
        },
        {
          key: ROUTES.accounting.amisAccountObjects,
          label: "Khách hàng/NCC",
          onClick: () => onNavigate(ROUTES.accounting.amisAccountObjects),
        },
        {
          key: ROUTES.accounting.amisBankAccounts,
          label: "Tài khoản ngân hàng",
          onClick: () => onNavigate(ROUTES.accounting.amisBankAccounts),
        },
        {
          key: ROUTES.accounting.amisSyncLogs,
          label: "Nhật ký đồng bộ",
          onClick: () => onNavigate(ROUTES.accounting.amisSyncLogs),
        },
        {
          key: ROUTES.accounting.amisOutbox,
          label: "Dữ liệu đồng bộ",
          onClick: () => onNavigate(ROUTES.accounting.amisOutbox),
        },
        // TCR-2523 pha 2 — kéo tồn kho từ AMIS + sinh file số dư đầu kỳ.
        // Gác theo đúng key quyền BE dùng cho API (`accounting.amis_sync.view`).
        ...(hasPermission("accounting.amis_sync.view")
          ? [
              {
                key: ROUTES.accounting.amisInventoryBalance,
                label: "Tồn kho AMIS",
                onClick: () => onNavigate(ROUTES.accounting.amisInventoryBalance),
              },
            ]
          : []),
      ]);
    }

    // Thiết lập kế toán: quyền xem/cấu hình danh mục và quyền sửa chữa dữ liệu
    // là hai phạm vi độc lập. Menu phải bám theo permission thực tế; nếu một
    // custom role được cấp accounting.settings.repair thì không được chặn thêm
    // bằng vai trò owner ở tầng sidebar (route/API vẫn có permission guard).
    const accountingSettingsChildren: SidebarMenuItems = [];
    if (hasPermission("accounting.settings.general")) {
      accountingSettingsChildren.push({
        key: ROUTES.accounting.settings,
        label: "⚙️ Cài đặt kế toán",
        onClick: () => onNavigate(ROUTES.accounting.settings),
      });
    }
    if (hasPermission("accounting.settings.repair")) {
      accountingSettingsChildren.unshift({
        key: ROUTES.accounting.dataRepair,
        label: "Repair dữ liệu kế toán",
        onClick: () => onNavigate(ROUTES.accounting.dataRepair),
      });
    }
    if (hasPermission("accounting.scenarios.manage")) {
      accountingSettingsChildren.push({
        key: ROUTES.accounting.documentScenarios,
        label: "Kịch bản chứng từ",
        onClick: () => onNavigate(ROUTES.accounting.documentScenarios),
      });
    }
    if (accountingSettingsChildren.length > 0) {
      pushAcctSection(
        "accounting-section-settings",
        "Thiết lập",
        accountingSettingsChildren,
      );
    }

    if (acctChildren.length > 0) {
      items.push({
        key: "accounting-group",
        icon: <AccountBookOutlined />,
        label: labelWithTier("Kế toán", "E"),
        drilldown: true,
        children: acctChildren,
        popupClassName: "erp-menu-popup-accounting",
      } as SidebarMenuItem);
    }
  }

  // ─── Lương (Payroll) — group riêng (TCR-1534: ẩn khỏi sidebar) ───────────
  if (PAYROLL_MENU_ENABLED && hasPermission("payroll.view")) {
    items.push({
      key: "payroll-group",
      icon: <WalletOutlined />,
      label: labelWithTier("Lương", "S"),
      drilldown: true,
      children: [
        {
          key: ROUTES.payroll.dashboard,
          label: "Tổng quan lương",
          onClick: () => onNavigate(ROUTES.payroll.dashboard),
        },
        {
          key: ROUTES.payroll.runs,
          label: "Bảng lương",
          onClick: () => onNavigate(ROUTES.payroll.runs),
        },
        {
          key: ROUTES.payroll.components,
          label: "Thành phần lương",
          onClick: () => onNavigate(ROUTES.payroll.components),
        },
        {
          key: ROUTES.payroll.reports.s5hkd,
          label: "Báo cáo S5-HKD",
          onClick: () => onNavigate(ROUTES.payroll.reports.s5hkd),
        },
        {
          key: ROUTES.payroll.reports.qttTncn,
          label: "Quyết toán TNCN",
          onClick: () => onNavigate(ROUTES.payroll.reports.qttTncn),
        },
        { type: "divider" as const },
        {
          key: ROUTES.me.payslips,
          label: "Phiếu lương của tôi",
          onClick: () => onNavigate(ROUTES.me.payslips),
        },
      ],
      disabled: !hasStarter,
      style: !hasStarter ? { opacity: 0.5, cursor: "not-allowed" } : undefined,
      title: !hasStarter ? "Cần gói Starter trở lên" : undefined,
    } as SidebarMenuItem);
  }

  const attendanceMenuItem = {
    key: ROUTES.hrm.attendances,
    icon: <AuditOutlined />,
    label: "Chấm công",
    onClick: () => onNavigate(ROUTES.hrm.attendances),
  };

  /*
   * TCR-1846 / TCR-1813 — gác TỪNG MỤC thay vì gác cả khối.
   *
   * Trước đây cả menu Nhân sự nằm sau đúng một điều kiện
   * `hasPermission(['hrm.dashboard.view'])`. Hai hướng đều sai:
   *  - Role chỉ có một nhánh hẹp (vd `hrm.contracts.view`) KHÔNG thấy menu Nhân sự,
   *    dù vào thẳng URL vẫn qua route guard.
   *  - Ngược lại, ai có `staff.view` là thấy TOÀN BỘ menu kể cả nhánh họ không vào được,
   *    bấm vào chỉ nhận trang 403 (đúng phiếu TCR-1846).
   *
   * Mỗi mục dưới đây mang ĐÚNG cặp quyền mà route guard của nó dùng trong `App.tsx`.
   * Giữ nguyên cặp `hrm.* | staff.*` chứ không bỏ vế `staff.*`: menu phải phản ánh
   * đúng thứ người dùng vào được, nếu lệch thì lại sinh ra sai khác kiểu cũ. Việc gỡ
   * hẳn `staff.*` là đổi ở route/decorator + backfill, không phải ở đây.
   *
   * Nhóm nào không còn mục con thì tự biến mất, và cả khối Nhân sự chỉ hiện khi còn ít
   * nhất một nhóm.
   */
  const hrmItem = (
    permissions: string[],
    item: SidebarMenuItem,
  ): SidebarMenuItem | null => (hasPermission(permissions) ? item : null);

  const hrmGroup = (
    group: { key: string; icon: JSX.Element; label: string },
    children: Array<SidebarMenuItem | null>,
  ): SidebarMenuItem | null => {
    const visible = children.filter(
      (child): child is SidebarMenuItem => child !== null,
    );
    return visible.length
      ? ({ ...group, children: visible } as SidebarMenuItem)
      : null;
  };

  {
    const hrmChildren: SidebarMenuItems = [
      hrmGroup(
        {
          key: "hrm-overview-group",
          icon: <DashboardOutlined />,
          label: "Tổng quan",
        },
        [
          hrmItem(["hrm.dashboard.view"], {
            key: ROUTES.hrm.dashboard,
            icon: <DashboardOutlined />,
            label: "Dashboard",
            onClick: () => onNavigate(ROUTES.hrm.dashboard),
          }),
        ],
      ),
      hrmGroup(
        {
          key: "hrm-organization-group",
          icon: <TeamOutlined />,
          label: "Tổ chức & hồ sơ",
        },
        [
          hrmItem(["hrm.employees.view"], {
            key: ROUTES.hrm.employees,
            icon: <TeamOutlined />,
            label: "Danh sách nhân sự",
            onClick: () => onNavigate(ROUTES.hrm.employees),
          }),
          hrmItem(["hrm.recruitment.view"], {
            key: ROUTES.hrm.recruitment.list,
            icon: <UserOutlined />,
            label: "Tuyển dụng",
            onClick: () => onNavigate(ROUTES.hrm.recruitment.list),
          }),
          // Mục riêng cho ứng viên: ban tuyển dụng cần xem ứng viên theo CHIỀU NGANG
          // (toàn bộ, lọc theo phòng ban/vị trí), không phải mở từng yêu cầu một.
          hrmItem(["hrm.recruitment.view"], {
            key: ROUTES.hrm.recruitment.candidates,
            icon: <IdcardOutlined />,
            label: "Ứng viên",
            onClick: () => onNavigate(ROUTES.hrm.recruitment.candidates),
          }),
          hrmItem(["hrm.recruitment.view"], {
            key: ROUTES.hrm.recruitment.approvals,
            icon: <AuditOutlined />,
            label: "Tờ trình tuyển dụng",
            onClick: () => onNavigate(ROUTES.hrm.recruitment.approvals),
          }),
          hrmItem(["hrm.org.view"], {
            // Gộp 4 mục cũ (Phòng ban / Bộ phận / Vị trí / Sơ đồ tổ chức) về MỘT.
            // Cả bốn vốn đọc cùng ba tập dữ liệu; nay là ba chế độ xem trong một trang.
            key: ROUTES.hrm.positions,
            icon: <ApartmentOutlined />,
            label: "Cơ cấu tổ chức",
            onClick: () => onNavigate(ROUTES.hrm.positions),
          }),
          hrmItem(["hrm.legal_entities.view"], {
            key: ROUTES.hrm.legalEntities,
            icon: <BankOutlined />,
            label: "Pháp nhân",
            onClick: () => onNavigate(ROUTES.hrm.legalEntities),
          }),
          hrmItem(["hrm.contracts.view"], {
            key: ROUTES.hrm.contracts,
            icon: <ContactsOutlined />,
            label: "Hợp đồng lao động",
            onClick: () => onNavigate(ROUTES.hrm.contracts),
          }),
          hrmItem(["hrm.internal_documents.view"], {
            key: ROUTES.hrm.internalDocuments,
            icon: <FileTextOutlined />,
            label: "Văn bản nội bộ",
            onClick: () => onNavigate(ROUTES.hrm.internalDocuments),
          }),
        ],
      ),
      hrmGroup(
        {
          key: "hrm-operations-group",
          icon: <AuditOutlined />,
          label: "Vận hành nhân sự",
        },
        [
          hrmItem(["hrm.attendance.view"], attendanceMenuItem),
          hrmItem(["hrm.schedules.view"], {
            key: ROUTES.hrm.schedules,
            icon: <CalendarOutlined />,
            label: "Lịch ca",
            onClick: () => onNavigate(ROUTES.hrm.schedules),
          }),
          hrmItem(["hrm.overtime.view"], {
            key: ROUTES.hrm.overtime,
            icon: <CalendarOutlined />,
            label: "Tăng ca",
            onClick: () => onNavigate(ROUTES.hrm.overtime),
          }),
          hrmItem(["hrm.timeoff.view"], {
            key: ROUTES.hrm.timeoffs,
            icon: <AuditOutlined />,
            label: "Nghỉ phép",
            onClick: () => onNavigate(ROUTES.hrm.timeoffs),
          }),
          hrmItem(["hrm.requests.view"], {
            key: ROUTES.hrm.requests,
            icon: <FileTextOutlined />,
            label: "Đề xuất",
            onClick: () => onNavigate(ROUTES.hrm.requests),
          }),
          hrmItem(["hrm.change_requests.view"], {
            key: ROUTES.hrm.employeeChangeRequests,
            icon: <FileTextOutlined />,
            label: "Đề xuất thay đổi cá nhân",
            onClick: () => onNavigate(ROUTES.hrm.employeeChangeRequests),
          }),
        ],
      ),
      hrmGroup(
        {
          key: "hrm-performance-group",
          icon: <LineChartOutlined />,
          label: "Hiệu suất & lương",
        },
        [
          hrmItem(["hrm.kpi.view"], {
            key: ROUTES.hrm.kpis,
            icon: <LineChartOutlined />,
            label: "KPI (phiếu BSC)",
            onClick: () => onNavigate(ROUTES.hrm.kpis),
          }),
          hrmItem(["hrm.kpi.manage"], {
            key: ROUTES.hrm.kpiScorecardImport,
            icon: <LineChartOutlined />,
            label: "Nhập phiếu BSC-KPI",
            onClick: () => onNavigate(ROUTES.hrm.kpiScorecardImport),
          }),
          hrmItem(["hrm.payroll.view"], {
            key: ROUTES.hrm.payrolls,
            icon: <DollarOutlined />,
            label: "Bảng lương",
            onClick: () => onNavigate(ROUTES.hrm.payrolls),
          }),
          hrmItem(["hrm.benefits.view"], {
            key: ROUTES.hrm.benefits,
            icon: <DollarOutlined />,
            label: "Phúc lợi",
            onClick: () => onNavigate(ROUTES.hrm.benefits),
          }),
          hrmItem(["hrm.insurance.view"], {
            key: ROUTES.hrm.insuranceRoster,
            icon: <SafetyCertificateOutlined />,
            label: "BHXH",
            onClick: () => onNavigate(ROUTES.hrm.insuranceRoster),
          }),
        ],
      ),
      hrmGroup(
        {
          key: "hrm-configuration-group",
          icon: <SettingOutlined />,
          label: "Cấu hình",
        },
        [
          hrmItem(["hrm.settings.view"], {
            key: ROUTES.hrm.settings,
            icon: <SettingOutlined />,
            label: "Cấu hình chung",
            onClick: () => onNavigate(ROUTES.hrm.settings),
          }),
          hrmItem(["hrm.settings.view"], {
            key: ROUTES.hrm.attendancePolicy,
            icon: <CalendarOutlined />,
            label: "Chính sách chấm công",
            onClick: () => onNavigate(ROUTES.hrm.attendancePolicy),
          }),
          // TCR-2366: đơn Base nào tính vào Nghỉ phép / Công thực / Nghỉ khác.
          hrmItem(["hrm.attendance.view"], {
            key: ROUTES.hrm.attendanceConfig,
            icon: <SettingOutlined />,
            label: "Cấu hình chấm công",
            onClick: () => onNavigate(ROUTES.hrm.attendanceConfig),
          }),
          hrmItem(["hrm.settings.view"], {
            key: ROUTES.hrm.assetTypes,
            icon: <TagsOutlined />,
            label: "Loại tài sản",
            onClick: () => onNavigate(ROUTES.hrm.assetTypes),
          }),
          hrmItem(["hrm.settings.view"], {
            key: ROUTES.hrm.documentTypes,
            icon: <FileTextOutlined />,
            label: "Loại văn bản",
            onClick: () => onNavigate(ROUTES.hrm.documentTypes),
          }),
          hrmItem(["hrm.settings.view"], {
            key: ROUTES.hrm.workRegions,
            icon: <GlobalOutlined />,
            label: "Khu vực làm việc",
            onClick: () => onNavigate(ROUTES.hrm.workRegions),
          }),
          hrmItem(["hrm.offices.view"], {
            key: ROUTES.hrm.offices,
            icon: <BankOutlined />,
            label: "Văn phòng",
            onClick: () => onNavigate(ROUTES.hrm.offices),
          }),
          hrmItem(["hrm.basevn.view"], {
            key: ROUTES.hrm.basevn,
            icon: <SettingOutlined />,
            label: "Cấu hình Base.vn",
            onClick: () => onNavigate(ROUTES.hrm.basevn),
          }),
          hrmItem(["hrm.basevn.view"], {
            key: ROUTES.hrm.basevnSyncLog,
            icon: <HistoryOutlined />,
            label: "Nhật ký đồng bộ Base.vn",
            onClick: () => onNavigate(ROUTES.hrm.basevnSyncLog),
          }),
        ],
      ),
      hrmGroup(
        {
          key: "hrm-reports-group",
          icon: <LineChartOutlined />,
          label: "Báo cáo",
        },
        [
          // TCR-1846 — trước đây 2 mục này gác bằng role literal `isManagerPlus`, trong khi route
          // guard của chúng dùng `hrm.reports.view`. Lệch nhau nghĩa là nv_hr (CÓ
          // `hrm.reports.view`) không thấy mục menu nhưng vào thẳng URL vẫn được. Gác
          // theo đúng quyền như các mục còn lại.
          hrmItem(["hrm.reports.view"], {
            key: ROUTES.hrm.turnoverReport,
            icon: <LineChartOutlined />,
            label: "Biến động nhân sự",
            onClick: () => onNavigate(ROUTES.hrm.turnoverReport),
          }),
          hrmItem(["hrm.reports.view"], {
            key: ROUTES.hrm.trendReport,
            icon: <LineChartOutlined />,
            label: "Xu hướng nhân sự",
            onClick: () => onNavigate(ROUTES.hrm.trendReport),
          }),
          // TCR-2364 — tách khỏi màn Biến động nhân sự thành mục riêng.
          hrmItem(["hrm.reports.view"], {
            key: ROUTES.hrm.policyReportDocuments,
            icon: <FileTextOutlined />,
            label: "Báo cáo hệ thống chính sách",
            onClick: () => onNavigate(ROUTES.hrm.policyReportDocuments),
          }),
          hrmItem(["hrm.workflows.view"], {
            key: ROUTES.hrm.workflowsReport,
            icon: <LineChartOutlined />,
            label: "Báo cáo quy trình",
            onClick: () => onNavigate(ROUTES.hrm.workflowsReport),
          }),
          hrmItem(["hrm.weworks.view"], {
            key: ROUTES.hrm.weworksReport,
            icon: <LineChartOutlined />,
            label: "Báo cáo công việc",
            onClick: () => onNavigate(ROUTES.hrm.weworksReport),
          }),
          hrmItem(["hrm.requests.view"], {
            key: ROUTES.hrm.requestsReport,
            icon: <LineChartOutlined />,
            label: "Báo cáo đề xuất",
            onClick: () => onNavigate(ROUTES.hrm.requestsReport),
          }),
          hrmItem(["hrm.schedules.view"], {
            key: ROUTES.hrm.schedulesReport,
            icon: <LineChartOutlined />,
            label: "Báo cáo lịch ca",
            onClick: () => onNavigate(ROUTES.hrm.schedulesReport),
          }),
          hrmItem(["hrm.timeoff.view"], {
            key: ROUTES.hrm.timeoffsReport,
            icon: <LineChartOutlined />,
            label: "Báo cáo nghỉ phép",
            onClick: () => onNavigate(ROUTES.hrm.timeoffsReport),
          }),
        ],
      ),
    ].filter((group): group is SidebarMenuItem => group !== null);

    items.push({
      key: "hrm-group",
      icon: <TeamOutlined />,
      ...maybeWidePopup("Nhân sự", hrmChildren),
      drilldown: true,
      children: hrmChildren,
    } as SidebarMenuItem);
  }

  /*
   * E-Office — Quy trình và Công việc, tách khỏi menu Nhân sự.
   *
   * Hai màn này vốn nằm trong nhóm "Quy trình" của Nhân sự nhưng không phải
   * nghiệp vụ nhân sự: chúng là luồng duyệt và giao việc dùng chung cho mọi
   * phòng ban. Để trong Nhân sự thì người ngoài phòng nhân sự không nghĩ là
   * mình có quyền vào.
   *
   * Quyền GIỮ NGUYÊN `hrm.workflows.view` / `hrm.weworks.view` — đúng cặp mà
   * route guard đang dùng. Đổi tên quyền là việc của backend + backfill, làm ở
   * đây chỉ khiến menu và route lệch nhau.
   *
   * Không có mục con nào xem được thì cả menu tự ẩn, giống mọi khối khác.
   */
  {
    const eOfficeChildren: SidebarMenuItems = [
      hrmItem(["hrm.workflows.view"], {
        key: ROUTES.eOffice.workflows,
        icon: <PartitionOutlined />,
        label: "Quy trình",
        onClick: () => onNavigate(ROUTES.eOffice.workflows),
      }),
      hrmItem(["hrm.weworks.view"], {
        key: ROUTES.eOffice.weworks,
        icon: <ProjectOutlined />,
        label: "Công việc",
        onClick: () => onNavigate(ROUTES.eOffice.weworks),
      }),
    ].filter((item): item is SidebarMenuItem => item !== null);

    if (eOfficeChildren.length) {
      items.push({
        key: "e-office-group",
        icon: <AppstoreOutlined />,
        ...maybeWidePopup("E-Office", eOfficeChildren),
        drilldown: true,
        children: eOfficeChildren,
      } as SidebarMenuItem);
    }
  }
  // TCR-1702 — TRƯỚC ĐÂY ở đây có nhánh `else` dựng nguyên một khối menu "Nhân sự" chỉ chứa
  // "Chấm công" cho MỌI tài khoản KHÔNG có quyền HRM. Hậu quả đúng như phiếu báo: nhân viên thủ
  // kho (role `inventory`, 60 quyền, KHÔNG có `hrm.*` lẫn `staff.view`) vẫn thấy menu Nhân sự.
  //
  // Bỏ hẳn nhánh đó thay vì gác thêm điều kiện: từ 2026-09-10 màn Chấm công đã đóng lại theo
  // `hrm.attendance.view` ở CẢ route lẫn API, nên với nhóm này mục menu chỉ dẫn tới một trang
  // 403. Phần dữ liệu "của chính tôi" sẽ nằm ở màn tự phục vụ RIÊNG, không mượn menu Nhân sự.

  // ─── §15 Cài đặt — group lớn nhất, multi-col popup ────────────────────────
  // Gom: Doanh nghiệp, Chi nhánh, Nhân viên, Phần cứng, Giao diện, Phân quyền,
  // Loyalty, Thông báo, Locale, Billing, Profile, Security, API Keys, Audit,
  // Import dữ liệu, Hỗ trợ.
  const settingsChildren: SidebarMenuItems = [];

  // — Doanh nghiệp —
  if (hasPermission("settings.business")) {
    settingsChildren.push({
      key: ROUTES.settings.business,
      icon: <ShopOutlined />,
      label: "Doanh nghiệp",
      onClick: () => onNavigate(ROUTES.settings.business),
    });
  }
  // TCR-1592 — trước đây push không điều kiện, hiện với MỌI role kể cả thủ kho. Gate theo
  // 'settings.manage' (key sẵn có, chỉ owner qua wildcard + manager có — đúng nhóm role thực
  // sự cần cấu hình chuỗi chi nhánh, nhất quán với các mục Cài đặt khác trong nhóm này).
  // TCR-1852 — 'settings.manage' đơn độc bỏ sót đúng nhóm role được cấp riêng quyền xem chi
  // nhánh ('stores.view'/'stores.read', 2 key thật trong catalogue nhóm "Chi nhánh") mà không
  // có settings.manage — role đó vẫn nên thấy menu này. OR thêm 2 key đó vào gate.
  if (hasPermission(["settings.manage", "stores.view", "stores.read"]))
    settingsChildren.push({
      key: `${ROUTES.stores.list}__shortcut`,
      icon: <ShopOutlined />,
      label: labelWithTier("Chuỗi chi nhánh", "P"),
      onClick: () => onNavigate(ROUTES.stores.list),
    });
  if (hasPermission(["hrm.accounts.view"])) {
    settingsChildren.push({
      key: ROUTES.staff.list,
      icon: <TeamOutlined />,
      label: "Tài khoản ERP",
      onClick: () => onNavigate(ROUTES.staff.list),
    });
  }
  if (hasPermission("settings.payroll")) {
    settingsChildren.push({
      key: ROUTES.settings.payroll,
      icon: <WalletOutlined />,
      label: labelWithTier("Cài đặt lương", "S"),
      onClick: () => onNavigate(ROUTES.settings.payroll),
    });
  }
  if (hasPermission("settings.roles")) {
    settingsChildren.push({
      key: ROUTES.settings.roles,
      icon: <SafetyCertificateOutlined />,
      label: "Vai trò & Quyền",
      onClick: () => onNavigate(ROUTES.settings.roles),
    });
  }
  // — Phần cứng & Giao diện —
  settingsChildren.push({ type: "divider" as const });
  if (hasPermission("settings.hardware")) {
    settingsChildren.push({
      key: ROUTES.settings.hardware,
      icon: <PrinterOutlined />,
      label: labelWithTier("Phần cứng", "P"),
      onClick: () => onNavigate(ROUTES.settings.hardware),
    });
  }
  // Mẫu hoá đơn / tem là màn hình cài đặt, không phải màn vận hành in tại quầy.
  // Gate bằng `settings.manage` để thủ kho/NV kho không thấy menu và không bị dẫn tới
  // màn hình cài đặt mà họ không được cấp quyền. API đọc template vẫn mở riêng cho POS
  // dùng template mặc định để in phiếu/hoá đơn; không dùng permission này để chặn runtime POS.
  if (hasPermission("settings.manage")) {
    settingsChildren.push({
      key: ROUTES.settings.receiptTemplates,
      icon: <PrinterOutlined />,
      label: "Mẫu hoá đơn / tem",
      onClick: () => onNavigate(ROUTES.settings.receiptTemplates),
    });
  }
  if (hasPermission("settings.theme")) {
    settingsChildren.push({
      key: ROUTES.settings.appearance,
      icon: <BgColorsOutlined />,
      label: "Giao diện",
      onClick: () => onNavigate(ROUTES.settings.appearance),
    });
  }

  if (hasPermission("settings.notifications")) {
    settingsChildren.push({
      key: ROUTES.settings.notifications,
      icon: <NotificationOutlined />,
      label: "Thông báo",
      onClick: () => onNavigate(ROUTES.settings.notifications),
    });
  }
  if (isOwner) {
    settingsChildren.push({
      key: ROUTES.settings.general,
      icon: <CarOutlined />,
      label: "Cấu hình chung",
      onClick: () => onNavigate(ROUTES.settings.general),
    });
  }
  if (hasPermission("settings.locale")) {
    settingsChildren.push({
      key: ROUTES.settings.locale,
      icon: <GlobalOutlined />,
      label: "Múi giờ hạch toán",
      onClick: () => onNavigate(ROUTES.settings.locale),
    });
  }

  // — Thanh toán & HĐĐT —
  // TCR-1592 — 3 mục dưới đây (Phương thức thanh toán, Cài đặt HĐĐT, Import dữ liệu) trước đây
  // push không điều kiện, hiện với MỌI role kể cả thủ kho dù API GET đứng sau (vd
  // settings.controller.ts getPayment) cũng không đòi permission riêng — đây thuần là quyết
  // định UX (ẩn khỏi role không cần cấu hình), gate theo 'settings.manage' như "Chuỗi chi
  // nhánh" ở trên cho nhất quán.
  if (hasPermission("settings.manage")) {
    settingsChildren.push({ type: "divider" as const });
    settingsChildren.push({
      key: `${ROUTES.finance.paymentMethods}__shortcut`,
      icon: <BarcodeOutlined />,
      label: "Phương thức thanh toán",
      onClick: () => onNavigate(ROUTES.finance.paymentMethods),
    });
    settingsChildren.push({
      key: `${ROUTES.finance.einvoiceSettings}__shortcut`,
      icon: <BankOutlined />,
      label: labelWithTier("Cài đặt HĐĐT", "S"),
      onClick: () => onNavigate(ROUTES.finance.einvoiceSettings),
    });
  }
  if (hasPermission("data_export.manage")) {
    settingsChildren.push({
      key: ROUTES.settings.dataExport,
      icon: <DatabaseOutlined />,
      label: "Xuất dữ liệu",
      onClick: () => onNavigate(ROUTES.settings.dataExport),
    });
  }
  if (hasPermission("settings.manage"))
    settingsChildren.push({
      key: `${ROUTES.imports.index}__shortcut`,
      icon: <DatabaseOutlined />,
      label: labelWithTier("Import dữ liệu", "S"),
      onClick: () => onNavigate(ROUTES.imports.index),
    });

  // — Bảo mật & API —
  // TCR-1483 (bảo mật) — API Keys và Audit log/Nhật ký hệ thống trước đây hiện cho
  // MỌI role dù BE nay đã chặn owner-only (ApiKeysController) / owner+manager
  // (ActionLogController) — gate lại ở FE để tránh hiện menu rồi bấm vào bị 403.
  settingsChildren.push({ type: "divider" as const });
  if (isOwner) {
    settingsChildren.push({
      key: ROUTES.settings.apiKeys,
      icon: <KeyOutlined />,
      label: labelWithTier("API Keys", "E"),
      onClick: () => onNavigate(ROUTES.settings.apiKeys),
    });
  }
  if (isManagerPlus) {
    settingsChildren.push({
      key: ROUTES.settings.auditLog,
      icon: <AuditOutlined />,
      label: labelWithTier("Audit log", "P"),
      onClick: () => onNavigate(ROUTES.settings.auditLog),
    });
  }
  // Trạng thái migration là hạ tầng dùng chung cho cả database (ledger không có `tenant_id`),
  // không phải dữ liệu của riêng tenant như Audit log — nên owner-only, hẹp hơn Audit log.
  if (isOwner) {
    settingsChildren.push({
      key: ROUTES.settings.migrations,
      icon: <DatabaseOutlined />,
      label: "Log migration",
      onClick: () => onNavigate(ROUTES.settings.migrations),
    });
  }

  // — Cá nhân —
  settingsChildren.push({ type: "divider" as const });
  settingsChildren.push({
    key: ROUTES.settings.profile,
    icon: <UserOutlined />,
    label: "Hồ sơ cá nhân",
    onClick: () => onNavigate(ROUTES.settings.profile),
  });
  settingsChildren.push({
    key: ROUTES.settings.security,
    icon: <LockOutlined />,
    label: "Bảo mật",
    onClick: () => onNavigate(ROUTES.settings.security),
  });

  // — Hỗ trợ —
  // TCR-1743 — "Trung tâm hỗ trợ" đã gỡ khỏi menu VÀ khỏi router (App.tsx): trang mới chỉ có
  // khung tĩnh, click vào không dùng được gì và chưa qua test, PO yêu cầu ẩn hẳn. Chỉ còn
  // "Changelog" ở nhóm này. Muốn bật lại: khôi phục mục dưới đây + route index của `support`.
  settingsChildren.push({ type: "divider" as const });
  settingsChildren.push({
    key: ROUTES.support.changelog,
    icon: <AppstoreOutlined />,
    label: "Changelog",
    onClick: () => onNavigate(ROUTES.support.changelog),
  });
  if (isManagerPlus) {
    // TCR-1483 (bảo mật) — trang này gọi API `/action-logs` (ActionLogController),
    // BE nay đã chặn owner+manager — gate FE theo đúng phạm vi role đó.
    settingsChildren.push({
      key: ROUTES.system.actionLogs,
      icon: <AuditOutlined />,
      label: labelWithTier("Nhật ký hệ thống", "P"),
      onClick: () => onNavigate(ROUTES.system.actionLogs),
    });
  }
  if (isManagerPlus) {
    settingsChildren.push({
      key: ROUTES.settings.workerMonitor,
      icon: <CloudServerOutlined />,
      label: "Theo dõi Worker",
      onClick: () => onNavigate(ROUTES.settings.workerMonitor),
    });
  }
  if (isManagerPlus) {
    // TCR-2653 — "Giám sát hệ thống": hiệu năng API (độ trễ, lỗi, bottleneck).
    settingsChildren.push({
      key: ROUTES.system.opsDashboard,
      icon: <DashboardOutlined />,
      label: "Giám sát hệ thống",
      onClick: () => onNavigate(ROUTES.system.opsDashboard),
    });
  }

  items.push({
    key: "/settings",
    icon: <SettingOutlined />,
    ...maybeWidePopup("Cài đặt", settingsChildren),
    drilldown: true,
    children: settingsChildren,
  } as SidebarMenuItem);

  return dropEmptyGroups(items);
}

/**
 * TCR-1687 — bỏ mọi group không còn mục con nào sau khi lọc quyền.
 *
 * Bug gốc: menu "ECom" vẫn hiện (kèm mũi tên expand, bấm vào trống trơn) cho `b2b_sales` — role
 * không có bất kỳ quyền `ecom.*` nào. Nguyên nhân không phải children thiếu gate (chúng gate
 * đúng cả) mà là gate NGOÀI của khối cố ý rộng: nó OR thêm `orders.view`/`settings.manage` để
 * không phải lặp điều kiện cho từng mục con. Role lọt qua gate ngoài rồi push group với
 * `children: []`, và antd vẫn vẽ submenu vì `Array.isArray(children)` là true.
 *
 * Lọc tập trung ở đây thay vì rải `if (children.length > 0)` quanh từng chỗ push: ticket yêu cầu
 * "check tương tự với các menu khác" (cùng lỗi có ở `fulfillment-group`, `b2b-group`), và cách
 * này còn đúng luôn cho mọi group thêm về sau mà không cần nhớ guard. Đệ quy vì group con lồng
 * trong group cha (vd nhóm Đối soát trong ECom) cũng rỗng được.
 *
 * Không đụng tới item KHÔNG có key `children` (mục lá) — chỉ group mới bị soi.
 */
function dropEmptyGroups(items: SidebarMenuItems): SidebarMenuItems {
  const ketQua: SidebarMenuItems = [];

  for (const item of items) {
    if (!item || !("children" in item) || !Array.isArray(item.children)) {
      ketQua.push(item);
      continue;
    }

    const conConLai = dropEmptyGroups(item.children as SidebarMenuItems);
    if (conConLai.length === 0) continue;

    ketQua.push({ ...item, children: conConLai } as SidebarMenuItem);
  }

  return ketQua;
}
